import * as THREE from "three";
import { isUseLocalModel } from "@/utils/link-detection";
import { APP_ASSETS } from "@/types/supported-venue";

const MODEL_BASE_URL = isUseLocalModel()
  ? "/model/navigation/"
  : `${APP_ASSETS}/venue/xiqu/`;
export const GLB_AVATAR = isUseLocalModel()
  ? MODEL_BASE_URL + "robot.glb"
  : `${APP_ASSETS}/navigation/avatar/robot.glb`;

export const getNearestNumber = (
  dict: Record<number, number>,
  targetPercentage: number,
): number => {
  const entries = Object.entries(dict);
  if (entries.length === 0) return 0;
  const nearest = entries.reduce(
    (closest, [keyStr, value]) => {
      const key = parseFloat(keyStr);
      const diff = Math.abs(key - targetPercentage);
      const closestDiff = Math.abs(closest.key - targetPercentage);
      return diff < closestDiff ? { key, value: Number(value) } : closest;
    },
    { key: parseFloat(entries[0][0]), value: Number(entries[0][1]) },
  );
  return nearest.value;
};

export const getFloorNumber = (
  dict: Record<number, number>,
  targetPercentage: number,
) => {
  const entries = Object.entries(dict);
  if (entries.length === 0) return 0;

  if (targetPercentage == 0) {
    return entries[0][1];
  }
  if (targetPercentage == 1) {
    return entries[entries.length - 1][1];
  }
  for (let i = 0; i < entries.length; i++) {
    if (i == entries.length - 1) {
      return entries[entries.length - 1][1];
    }
    const nextKey = parseFloat(entries[i + 1][0]);
    const currentKey = parseFloat(entries[i][0]);
    if (targetPercentage >= currentKey && targetPercentage < nextKey) {
      return entries[i][1];
    }
  }
  return 0;
};

export const getFloorIndex = (
  dict: Record<number, string>,
  targetPercentage: number,
) => {
  const entries = Object.entries(dict);
  if (entries.length === 0) return 0;
  if (targetPercentage == 0) {
    return 0;
  }
  if (targetPercentage == 1) {
    return entries.length - 1;
  }
  for (let i = 0; i < entries.length; i++) {
    if (i == entries.length - 1) {
      return entries.length - 1;
    }
    const nextKey = parseFloat(entries[i + 1][0]);
    const currentKey = parseFloat(entries[i][0]);
    if (targetPercentage >= currentKey && targetPercentage < nextKey) {
      return i;
    }
  }
  return 0;
};

export const getFloorStringWithIndex = (
  dict: Record<number, string>,
  targetPercentage: number,
) => {
  const entries = Object.entries(dict);
  if (entries.length === 0) return "";

  if (targetPercentage == 0) {
    return entries[0][1];
  }
  if (targetPercentage == 1) {
    // return entries[entries.length - 1][1];
    return {
      index: entries.length - 1,
      string: entries[entries.length - 1][1],
    };
  }
  for (let i = 0; i < entries.length; i++) {
    if (i == entries.length - 1) {
      // return entries[entries.length - 1][1];
      return {
        index: entries.length - 1,
        string: entries[entries.length - 1][1],
      };
    }
    const nextKey = parseFloat(entries[i + 1][0]);
    const currentKey = parseFloat(entries[i][0]);
    if (targetPercentage >= currentKey && targetPercentage < nextKey) {
      // return entries[i][1];
      return {
        index: i,
        string: entries[i][1],
      };
    }
  }
  return { index: 0, string: "" };
};

export const getFloorString = (
  dict: Record<number, string>,
  targetPercentage: number,
) => {
  const entries = Object.entries(dict);
  if (entries.length === 0) return "";

  if (targetPercentage == 0) {
    return entries[0][1];
  }
  if (targetPercentage == 1) {
    return entries[entries.length - 1][1];
  }
  for (let i = 0; i < entries.length; i++) {
    if (i == entries.length - 1) {
      return entries[entries.length - 1][1];
    }
    const nextKey = parseFloat(entries[i + 1][0]);
    const currentKey = parseFloat(entries[i][0]);
    if (targetPercentage >= currentKey && targetPercentage < nextKey) {
      return entries[i][1];
    }
  }
  return "";
};

export const getInterpolatedNumberArray = (
  dict: Record<number, number[]>,
  targetPercentage: number,
): number[] => {
  const sortedEntries = Object.entries(dict)
    .map(([keyStr, value]) => ({ key: parseFloat(keyStr), value }))
    .sort((a, b) => a.key - b.key);

  if (sortedEntries.length < 2) {
    return sortedEntries[0]?.value || [0, 0, 0];
  }

  // Find bracketing points (lower <= target <= upper)
  let lower = sortedEntries[0];
  let upper = sortedEntries[sortedEntries.length - 1];

  for (let i = 0; i < sortedEntries.length - 1; i++) {
    if (
      sortedEntries[i].key <= targetPercentage &&
      sortedEntries[i + 1].key >= targetPercentage
    ) {
      lower = sortedEntries[i];
      upper = sortedEntries[i + 1];
      break;
    }
  }

  // Clamp to bounds if outside range
  if (targetPercentage < sortedEntries[0].key) {
    return lower.value;
  }
  if (targetPercentage > sortedEntries[sortedEntries.length - 1].key) {
    return upper.value;
  }

  // Linear interpolation t = (target - lower) / (upper - lower)
  const t = (targetPercentage - lower.key) / (upper.key - lower.key);

  return [
    lower.value[0] + (upper.value[0] - lower.value[0]) * t,
    lower.value[1] + (upper.value[1] - lower.value[1]) * t,
    lower.value[2] + (upper.value[2] - lower.value[2]) * t,
  ];
};

export const getLowerNumberArray = (
  dict: Record<number, number[]>,
  targetPercentage: number,
): number[] => {
  const sortedEntries = Object.entries(dict)
    .map(([keyStr, value]) => ({ key: parseFloat(keyStr), value }))
    .sort((a, b) => a.key - b.key);

  if (sortedEntries.length === 0) {
    return [0, 0, 0];
  }

  // If target is below the smallest key, return the first value
  if (targetPercentage <= sortedEntries[0].key) {
    return sortedEntries[0].value;
  }

  // Find the lower bracket (largest key <= target)
  let lower = sortedEntries[0];
  for (let i = 0; i < sortedEntries.length; i++) {
    if (sortedEntries[i].key <= targetPercentage) {
      lower = sortedEntries[i];
    } else {
      break;
    }
  }

  return lower.value;
};

export const isLastNumberArray = (
  dict: Record<number, number[]>,
  targetPercentage: number,
): boolean => {
  const entries = Object.entries(dict);
  const entriesLength = entries.length - 1;
  const processingDict = entriesLength * targetPercentage;
  console.log(entriesLength + " " + processingDict);
  return processingDict > entriesLength - 2;
};

export const getFloorNumberArray = (
  dict: Record<number, number[]>,
  targetPercentage: number,
) => {
  const entries = Object.entries(dict);
  if (entries.length === 0) {
    return [0, 0, 0];
  }
  if (targetPercentage == 0) {
    return entries[0][1];
  }
  if (targetPercentage == 1) {
    return entries[entries.length - 1][1];
  }
  for (let i = 0; i < entries.length; i++) {
    if (i == entries.length - 1) {
      return entries[entries.length - 1][1];
    }
    const nextKey = parseFloat(entries[i + 1][0]);
    const currentKey = parseFloat(entries[i][0]);
    if (targetPercentage >= currentKey && targetPercentage < nextKey) {
      return entries[i][1];
    }
  }
  return [0, 0, 0];
};

const CHINESE_REGEX = /[\u4E00-\u9FFF]/;
export function hasChinese(obj: any): boolean {
  if (typeof obj === 'string') {
    return CHINESE_REGEX.test(obj);
  }
  if (Array.isArray(obj)) {
    return obj.some(hasChinese);
  }
  if (obj && typeof obj === 'object') {
    return Object.values(obj).some(hasChinese);
  }
  return false;
}

export const initCameraMovement = [
  [0, 0, 3],
  [0, 3.483927e-7, 3.000014],
  [0, 1.576858e-6, 3.000062],
  [0, 3.706425e-6, 3.000146],
  [0, 6.35475e-6, 3.000251],
  [0, 1.020057e-5, 3.000403],
  [2.384186e-7, 1.482982e-5, 3.000586],
  [2.384186e-7, 1.973081e-5, 3.000779],
  [4.768372e-7, 2.651218e-5, 3.001047],
  [7.152557e-7, 3.302277e-5, 3.001304],
  [1.192093e-6, 4.115247e-5, 3.001626],
  [1.66893e-6, 4.922217e-5, 3.001944],
  [2.384186e-6, 5.896267e-5, 3.002329],
  [3.099442e-6, 6.962474e-5, 3.00275],
  [4.291534e-6, 7.990719e-5, 3.003156],
  [5.722046e-6, 9.230999e-5, 3.003646],
  [7.390976e-6, 0.0001053802, 3.004162],
  [9.298325e-6, 0.0001193275, 3.004713],
  [1.168251e-5, 0.0001329713, 3.005252],
  [1.454353e-5, 0.0001488354, 3.005879],
  [1.764297e-5, 0.0001634284, 3.006455],
  [2.145767e-5, 0.0001809542, 3.007148],
  [2.622604e-5, 0.0001992565, 3.00787],
  [3.0756e-5, 0.000216394, 3.008547],
  [3.671646e-5, 0.0002358988, 3.009318],
  [4.339218e-5, 0.0002567052, 3.010139],
  [5.078316e-5, 0.0002780202, 3.010981],
  [5.841255e-5, 0.0002981497, 3.011776],
  [6.79493e-5, 0.0003213465, 3.012693],
  [7.843971e-5, 0.0003453491, 3.013641],
  [8.869171e-5, 0.0003673358, 3.014509],
  [0.0001022816, 0.0003941535, 3.015568],
  [0.0001163483, 0.0004203445, 3.016603],
  [0.0001301765, 0.0004447173, 3.017565],
  [0.0001471043, 0.0004726635, 3.018669],
  [0.0001657009, 0.0005016887, 3.019815],
  [0.0001854897, 0.000530957, 3.020971],
  [0.0002052784, 0.0005585165, 3.02206],
  [0.0002286434, 0.0005895888, 3.023287],
  [0.0002541542, 0.0006216238, 3.024552],
  [0.0002818108, 0.000654519, 3.025851],
  [0.0003085136, 0.0006846725, 3.027042],
  [0.0003404617, 0.0007193715, 3.028412],
  [0.0003750324, 0.0007549021, 3.029816],
  [0.0004122257, 0.0007915357, 3.031262],
  [0.0004470348, 0.0008242999, 3.032556],
  [0.0004897118, 0.0008627548, 3.034075],
  [0.0005347729, 0.0009014978, 3.035604],
  [0.000576973, 0.000936507, 3.036987],
  [0.0006272793, 0.0009763634, 3.038561],
  [0.0006816387, 0.001017813, 3.040197],
  [0.0007383823, 0.001059366, 3.041838],
  [0.0007989407, 0.001101939, 3.043519],
  [0.0008487701, 0.001135736, 3.044853],
  [0.0009236336, 0.001184904, 3.046794],
  [0.000995636, 0.001230147, 3.04858],
  [0.001062632, 0.001270864, 3.050188],
  [0.001142263, 0.00131761, 3.052033],
  [0.001225233, 0.001364692, 3.053892],
  [0.00131321, 0.001412786, 3.05579],
  [0.001406431, 0.001462045, 3.057734],
  [0.001492023, 0.001505968, 3.059468],
  [0.001583815, 0.001551546, 3.061267],
  [0.001701832, 0.00160835, 3.063508],
  [0.001814604, 0.001660755, 3.065576],
  [0.001920462, 0.001708526, 3.067461],
  [0.002043486, 0.001762406, 3.069587],
  [0.002173424, 0.001817549, 3.071763],
  [0.002290487, 0.001865939, 3.073672],
  [0.002430916, 0.001922234, 3.075893],
  [0.002574921, 0.001978364, 3.078107],
  [0.002712011, 0.002030376, 3.080158],
  [0.002869606, 0.002088501, 3.082451],
  [0.003035069, 0.002147926, 3.084795],
  [0.003208876, 0.002208547, 3.087185],
  [0.003371477, 0.00226388, 3.089367],
  [0.003561735, 0.002326867, 3.09185],
  [0.003757715, 0.002390059, 3.094341],
  [0.003937244, 0.002446502, 3.096566],
  [0.004149199, 0.002511459, 3.099126],
  [0.004342318, 0.002569253, 3.101404],
  [0.004569054, 0.00263552, 3.104015],
  [0.004802227, 0.002701924, 3.106632],
  [0.005047083, 0.002769967, 3.109312],
  [0.005271435, 0.002830874, 3.111711],
  [0.005533934, 0.002900539, 3.114455],
  [0.005774975, 0.002963089, 3.116918],
  [0.006055832, 0.003034267, 3.119721],
  [0.006340981, 0.003104906, 3.122502],
  [0.006605387, 0.00316904, 3.125026],
  [0.006912231, 0.003241836, 3.127891],
  [0.007234335, 0.003316497, 3.130829],
  [0.007555485, 0.00338934, 3.133695],
  [0.00785923, 0.003456843, 3.136351],
  [0.00820899, 0.003532963, 3.139344],
  [0.008545399, 0.003604664, 3.142164],
  [0.008911848, 0.0036812, 3.145173],
  [0.009291649, 0.003758828, 3.148224],
  [0.009682655, 0.003837144, 3.151302],
  [0.01004457, 0.003908232, 3.154095],
  [0.01046252, 0.003988774, 3.157259],
  [0.01088643, 0.004068813, 3.160403],
  [0.01132464, 0.004149959, 3.163589],
  [0.01172566, 0.004222841, 3.16645],
  [0.01219487, 0.004306585, 3.169737],
  [0.01267481, 0.004390548, 3.173032],
  [0.01316237, 0.004474272, 3.176316],
  [0.01361036, 0.004549822, 3.179279],
  [0.01413131, 0.004636165, 3.182664],
  [0.01466751, 0.004723368, 3.186082],
  [0.01515603, 0.004801426, 3.189141],
  [0.01571202, 0.004888774, 3.192563],
  [0.01628685, 0.004977489, 3.196037],
  [0.01688004, 0.005067397, 3.199557],
  [0.01748133, 0.005156969, 3.203063],
  [0.01803637, 0.005238264, 3.206244],
  [0.01867962, 0.005330957, 3.209869],
  [0.01932764, 0.005422729, 3.213458],
  [0.01993227, 0.005507007, 3.216752],
  [0.02062106, 0.005601447, 3.220442],
  [0.02132154, 0.0056959, 3.224131],
  [0.02204347, 0.00579165, 3.22787],
  [0.02269673, 0.005876954, 3.2312],
  [0.02344322, 0.005972931, 3.234944],
  [0.02421856, 0.00607102, 3.238769],
  [0.02500153, 0.006168521, 3.24257],
  [0.02568913, 0.006252889, 3.245858],
  [0.02659941, 0.006362877, 3.250142],
  [0.02735972, 0.006453299, 3.253662],
  [0.02821612, 0.006553688, 3.257569],
  [0.02909756, 0.006655414, 3.261526],
  [0.02995014, 0.006752376, 3.265296],
  [0.03090787, 0.006859693, 3.269467],
  [0.03180623, 0.006958833, 3.273317],
  [0.03272891, 0.00705922, 3.277215],
  [0.03369761, 0.007163134, 3.281247],
  [0.0346725, 0.007266193, 3.285244],
  [0.03566551, 0.00736973, 3.289257],
  [0.03666115, 0.007472083, 3.293222],
  [0.03769612, 0.007577047, 3.297287],
  [0.0387466, 0.007682123, 3.301353],
  [0.03990173, 0.007796053, 3.305759],
  [0.04098892, 0.007901778, 3.309845],
  [0.04209948, 0.008008356, 3.313962],
  [0.043257, 0.008117969, 3.318194],
  [0.04440975, 0.008225677, 3.322349],
  [0.0456295, 0.008338165, 3.326686],
  [0.04685068, 0.008449286, 3.330968],
  [0.04807281, 0.008559076, 3.335195],
  [0.04929399, 0.008667408, 3.339364],
  [0.05064082, 0.008785351, 3.343899],
  [0.05194402, 0.008898003, 3.348228],
  [0.05326629, 0.009010865, 3.352561],
  [0.0546155, 0.009124625, 3.356926],
  [0.0559907, 0.009239132, 3.361316],
  [0.0574069, 0.009355631, 3.365779],
  [0.05871844, 0.009462235, 3.36986],
  [0.06040573, 0.00959768, 3.375041],
  [0.06188583, 0.00971496, 3.379523],
  [0.06339765, 0.009833316, 3.384043],
  [0.06493235, 0.009952048, 3.388573],
  [0.06645799, 0.01006872, 3.393021],
  [0.06808138, 0.01019143, 3.397695],
  [0.0697155, 0.01031348, 3.40234],
  [0.07134247, 0.01043361, 3.406907],
  [0.07301998, 0.01055605, 3.411558],
  [0.07472539, 0.01067912, 3.416229],
  [0.07649398, 0.01080529, 3.421013],
  [0.07822561, 0.01092744, 3.425639],
  [0.08000827, 0.0110518, 3.430346],
  [0.08189559, 0.01118199, 3.435267],
  [0.08381724, 0.01131302, 3.440215],
  [0.08570242, 0.01144015, 3.445011],
  [0.08760428, 0.01156701, 3.449791],
  [0.08954191, 0.01169486, 3.454604],
  [0.09153819, 0.01182518, 3.459504],
  [0.09353352, 0.01195403, 3.464345],
  [0.09556103, 0.01208358, 3.469205],
  [0.09760737, 0.01221298, 3.474054],
  [0.09972, 0.01234518, 3.479003],
  [0.1018982, 0.01248005, 3.484045],
  [0.1040509, 0.01261194, 3.48897],
  [0.1062391, 0.01274465, 3.49392],
  [0.1084614, 0.01287805, 3.498889],
  [0.11092, 0.0130241, 3.504322],
  [0.1130583, 0.01314985, 3.508994],
  [0.1154368, 0.01328833, 3.514132],
  [0.1178386, 0.01342677, 3.519263],
  [0.1202607, 0.01356499, 3.524377],
  [0.1228216, 0.01370965, 3.529723],
  [0.1253002, 0.01384825, 3.534837],
  [0.1278434, 0.01398909, 3.540027],
  [0.1303816, 0.01412829, 3.545149],
  [0.132936, 0.01426705, 3.550247],
  [0.13556, 0.01440823, 3.555427],
  [0.1382511, 0.01455164, 3.56068],
  [0.1407084, 0.01468142, 3.565427],
  [0.1436033, 0.01483289, 3.570958],
  [0.146462, 0.01498101, 3.576359],
  [0.1492722, 0.01512525, 3.58161],
  [0.1521363, 0.0152709, 3.586903],
  [0.1551316, 0.01542181, 3.592378],
  [0.1580036, 0.01556517, 3.597571],
  [0.1610065, 0.01571373, 3.602942],
  [0.1639574, 0.0158584, 3.608164],
  [0.1669874, 0.01600564, 3.61347],
  [0.1700563, 0.01615345, 3.618786],
  [0.1732199, 0.01630447, 3.624208],
  [0.1763699, 0.01645353, 3.62955],
  [0.1795933, 0.01660473, 3.634959],
  [0.1830578, 0.01676576, 3.640708],
  [0.186311, 0.01691565, 3.646049],
  [0.1894827, 0.01706056, 3.651202],
  [0.1931126, 0.01722498, 3.657038],
  [0.1965027, 0.01737719, 3.662429],
  [0.1999707, 0.01753159, 3.667886],
  [0.2032874, 0.01767806, 3.673053],
  [0.2071095, 0.01784541, 3.678944],
  [0.2106566, 0.0179994, 3.684353],
  [0.2140052, 0.01814363, 3.689408],
  [0.2180188, 0.01831507, 3.695404],
  [0.2217202, 0.01847184, 3.700874],
  [0.2254674, 0.01862928, 3.706356],
  [0.2292366, 0.01878636, 3.711812],
  [0.2330468, 0.01894391, 3.717273],
  [0.2370484, 0.01910805, 3.722948],
  [0.2409477, 0.01926671, 3.72842],
  [0.2449672, 0.01942899, 3.734004],
  [0.2489886, 0.01959008, 3.739534],
  [0.2530994, 0.01975345, 3.745128],
  [0.2575152, 0.01992755, 3.751074],
  [0.2616785, 0.02009038, 3.75662],
  [0.2658837, 0.02025362, 3.762166],
  [0.2701354, 0.0204174, 3.767716],
  [0.274574, 0.02058709, 3.77345],
  [0.278897, 0.0207511, 3.778978],
  [0.2833214, 0.02091771, 3.784578],
  [0.2877436, 0.02108301, 3.790118],
  [0.2922692, 0.02125093, 3.795731],
  [0.2968109, 0.0214182, 3.801306],
  [0.301688, 0.02159649, 3.807231],
  [0.3063004, 0.02176386, 3.812776],
  [0.3109691, 0.02193205, 3.818332],
  [0.3139968, 0.02204048, 3.821906],
  [0.3206849, 0.02227829, 3.829718],
  [0.3256278, 0.02245254, 3.835421],
  [0.3305161, 0.02262363, 3.841003],
  [0.3354957, 0.02279671, 3.846633],
  [0.3404675, 0.02296832, 3.852196],
  [0.3455508, 0.02314255, 3.857827],
  [0.3506844, 0.0233173, 3.863455],
  [0.355804, 0.02349038, 3.86901],
  [0.3611102, 0.02366854, 3.87471],
  [0.3667824, 0.02385764, 3.880738],
  [0.3720977, 0.02403361, 3.886327],
  [0.3774738, 0.02421039, 3.891922],
  [0.3829582, 0.02438953, 3.897571],
  [0.3883994, 0.02456609, 3.903119],
  [0.3939919, 0.02474636, 3.908762],
  [0.3995421, 0.02492408, 3.914305],
  [0.4051528, 0.02510259, 3.919852],
  [0.4110112, 0.02528776, 3.925583],
  [0.4167531, 0.02546806, 3.931142],
  [0.4217134, 0.0256229, 3.935898],
  [0.4285204, 0.02583403, 3.942358],
  [0.4346116, 0.02602164, 3.948072],
  [0.4406164, 0.0262054, 3.953647],
  [0.4466803, 0.02638981, 3.959217],
  [0.4527183, 0.02657229, 3.964706],
  [0.4589338, 0.02675897, 3.970297],
  [0.4653192, 0.02694955, 3.97598],
  [0.4715297, 0.02713377, 3.981449],
  [0.4778771, 0.02732091, 3.98698],
  [0.4842734, 0.02750834, 3.992495],
  [0.491163, 0.02770896, 3.99837],
  [0.4976842, 0.02789768, 4.00387],
  [0.5041411, 0.02808344, 4.009258],
  [0.5107443, 0.02827228, 4.014711],
  [0.5173564, 0.02846029, 4.020112],
  [0.5243623, 0.02865829, 4.025774],
  [0.5310891, 0.0288473, 4.03115],
  [0.5380409, 0.02904148, 4.036646],
  [0.5448525, 0.02923067, 4.041973],
  [0.5518408, 0.02942365, 4.047379],
  [0.558846, 0.02961601, 4.05274],
  [0.5659499, 0.02980996, 4.058116],
  [0.5736003, 0.03001762, 4.063841],
  [0.5807438, 0.03021042, 4.069125],
  [0.5882173, 0.03041099, 4.074592],
  [0.595346, 0.03060125, 4.079748],
  [0.602762, 0.0307981, 4.085054],
  [0.6100969, 0.03099175, 4.090244],
  [0.6180897, 0.0312016, 4.095833],
  [0.6257024, 0.03140035, 4.101095],
  [0.6332037, 0.03159514, 4.106221],
  [0.6408784, 0.0317934, 4.111407],
  [0.6486137, 0.03199216, 4.116573],
  [0.6567006, 0.03219885, 4.121912],
  [0.6644177, 0.03239504, 4.126947],
  [0.6725192, 0.03259994, 4.132172],
  [0.680552, 0.03280204, 4.137291],
  [0.6885476, 0.03300218, 4.142326],
  [0.6966934, 0.03320503, 4.147397],
  [0.7054155, 0.03342111, 4.152759],
  [0.7136431, 0.03362387, 4.157754],
  [0.7220392, 0.03382974, 4.162791],
  [0.7305045, 0.03403627, 4.167807],
  [0.7388163, 0.03423805, 4.172672],
  [0.7473087, 0.03444321, 4.177583],
  [0.7558994, 0.03464973, 4.182489],
  [0.7650247, 0.03486801, 4.187634],
  [0.773705, 0.03507461, 4.192466],
  [0.7823851, 0.03528022, 4.197237],
  [0.7911956, 0.03548794, 4.202018],
  [0.7966623, 0.03561632, 4.204954],
  [0.8080387, 0.03588231, 4.21099],
  [0.8180099, 0.03611415, 4.2162],
  [0.827172, 0.03632612, 4.220921],
  [0.8362875, 0.03653604, 4.225556],
  [0.8461885, 0.03676298, 4.230521],
  [0.8554389, 0.03697399, 4.235096],
  [0.864866, 0.03718806, 4.239695],
  [0.8740151, 0.0373949, 4.244098],
  [0.8838468, 0.03761616, 4.248765],
  [0.8928728, 0.0378184, 4.25299],
  [0.9025717, 0.03803477, 4.257468],
  [0.9126053, 0.03825762, 4.262034],
  [0.9225502, 0.0384775, 4.266494],
  [0.9304082, 0.03865055, 4.269971],
  [0.9423637, 0.0389127, 4.275185],
  [0.9498501, 0.03907618, 4.278402],
  [0.961972, 0.03933975, 4.283536],
  [0.9719276, 0.03955523, 4.287683],
  [0.9827623, 0.03978872, 4.292127],
  [0.9930143, 0.0400087, 4.296264],
  [1.003346, 0.04022947, 4.300368],
  [1.013628, 0.04044827, 4.30439],
  [1.024221, 0.0406728, 4.308466],
  [1.034387, 0.04088738, 4.312316],
  [1.045036, 0.04111128, 4.316284],
  [1.055654, 0.04133361, 4.320175],
  [1.062261, 0.04147152, 4.322564],
  [1.076786, 0.04177353, 4.327728],
  [1.087698, 0.04199939, 4.33153],
  [1.098634, 0.04222484, 4.335273],
  [1.109553, 0.04244906, 4.338944],
  [1.120538, 0.0426738, 4.342573],
  [1.131658, 0.04290044, 4.346179],
  [1.143252, 0.04313583, 4.349869],
  [1.154571, 0.04336473, 4.353403],
  [1.165685, 0.04358869, 4.356807],
  [1.176999, 0.04381583, 4.360206],
  [1.188456, 0.04404502, 4.363582],
  [1.199753, 0.04427017, 4.366844],
  [1.211811, 0.04450962, 4.370255],
  [1.223166, 0.04473432, 4.373401],
  [1.234676, 0.04496127, 4.376523],
  [1.246816, 0.0451998, 4.379746],
  [1.258434, 0.04542728, 4.382764],
  [1.270114, 0.04565519, 4.385731],
  [1.282443, 0.04589493, 4.388792],
  [1.294631, 0.04613113, 4.391747],
  [1.306507, 0.04636049, 4.394559],
  [1.318595, 0.04659318, 4.397354],
  [1.330614, 0.04682377, 4.400065],
  [1.342814, 0.04705708, 4.402748],
  [1.355176, 0.04729273, 4.405398],
  [1.367352, 0.04752408, 4.40794],
  [1.379762, 0.04775913, 4.410462],
  [1.392467, 0.047999, 4.412973],
  [1.405214, 0.0482389, 4.415421],
  [1.417645, 0.04847214, 4.417738],
  [1.425579, 0.04862062, 4.419182],
  [1.443023, 0.0489461, 4.422261],
  [1.456585, 0.04919822, 4.424564],
  [1.469427, 0.0494362, 4.426672],
  [1.482332, 0.04967467, 4.42872],
  [1.495147, 0.04991077, 4.430684],
  [1.506149, 0.05011294, 4.432315],
  [1.521268, 0.05038993, 4.434474],
  [1.534176, 0.05062574, 4.436243],
  [1.547316, 0.05086507, 4.437974],
  [1.561163, 0.05111659, 4.439721],
  [1.574579, 0.05135959, 4.441339],
  [1.587965, 0.05160139, 4.442883],
  [1.600294, 0.0518235, 4.44424],
  [1.614613, 0.0520808, 4.44574],
  [1.628356, 0.05232706, 4.447104],
  [1.641993, 0.05257079, 4.448384],
  [1.655938, 0.05281936, 4.449618],
  [1.669721, 0.05306442, 4.450763],
  [1.683669, 0.05331178, 4.451847],
  [1.697632, 0.05355877, 4.452857],
  [1.711978, 0.05381189, 4.453817],
  [1.725803, 0.05405523, 4.454668],
  [1.739865, 0.05430213, 4.45546],
  [1.753891, 0.05454783, 4.456176],
  [1.768125, 0.05479659, 4.456826],
  [1.782366, 0.05504488, 4.457401],
  [1.796789, 0.05529575, 4.457908],
  [1.811298, 0.05554754, 4.45834],
  [1.826124, 0.05580425, 4.458701],
  [1.840393, 0.05605074, 4.458973],
  [1.854818, 0.05629941, 4.459172],
  [1.868936, 0.05654225, 4.459295],
  [1.884249, 0.0568051, 4.459346],
  [1.899185, 0.05706091, 4.459314],
  [1.913738, 0.05730963, 4.459208],
  [1.928348, 0.05755884, 4.459024],
  [1.943258, 0.05781265, 4.458759],
  [1.957961, 0.05806242, 4.458421],
  [1.973086, 0.05831887, 4.457994],
  [1.987888, 0.05856936, 4.457497],
  [2.002854, 0.05882216, 4.456918],
  [2.018936, 0.05909326, 4.456208],
  [2.034085, 0.05934817, 4.455458],
  [2.04883, 0.05959581, 4.454651],
  [2.064077, 0.05985145, 4.453738],
  [2.079486, 0.06010935, 4.452734],
  [2.09483, 0.06036572, 4.451653],
  [2.110036, 0.06061934, 4.450503],
  [2.12652, 0.06089381, 4.449168],
  [2.141822, 0.06114819, 4.447845],
  [2.157398, 0.0614067, 4.446417],
  [2.172694, 0.06166014, 4.444936],
  [2.18834, 0.06191901, 4.443338],
  [2.203647, 0.06217187, 4.441696],
  [2.219482, 0.06243308, 4.439915],
  [2.236501, 0.06271338, 4.437906],
  [2.252243, 0.06297228, 4.435963],
  [2.268156, 0.06323361, 4.433914],
  [2.28394, 0.06349248, 4.431799],
  [2.299722, 0.06375095, 4.429602],
  [2.316235, 0.06402105, 4.427214],
  [2.332075, 0.06427979, 4.424839],
  [2.348433, 0.06454669, 4.422299],
  [2.364718, 0.06481204, 4.419683],
  [2.380813, 0.065074, 4.417013],
  [2.396947, 0.06533627, 4.41425],
  [2.412835, 0.06559428, 4.411445],
  [2.429357, 0.06586227, 4.408442],
  [2.445404, 0.06612227, 4.405438],
  [2.461782, 0.06638738, 4.402287],
  [2.479103, 0.06666744, 4.398858],
  [2.49546, 0.06693166, 4.39553],
  [2.51162, 0.06719246, 4.392157],
  [2.52923, 0.06747638, 4.388384],
  [2.54572, 0.06774199, 4.384759],
  [2.562355, 0.06800973, 4.381012],
  [2.578982, 0.0682771, 4.377178],
  [2.595593, 0.06854401, 4.373257],
  [2.610311, 0.06878033, 4.369709],
  [2.629584, 0.06908958, 4.364954],
  [2.646394, 0.06935907, 4.360709],
  [2.663023, 0.0696255, 4.35642],
  [2.679799, 0.06989411, 4.352],
  [2.6967, 0.07016457, 4.347456],
  [2.713734, 0.070437, 4.342781],
  [2.730484, 0.07070472, 4.338092],
  [2.741231, 0.07087645, 4.335036],
  [2.764804, 0.07125289, 4.328199],
  [2.781851, 0.07152498, 4.323142],
  [2.798652, 0.07179305, 4.318064],
  [2.815858, 0.07206745, 4.312769],
  [2.83413, 0.07235873, 4.30704],
  [2.851393, 0.07263388, 4.301526],
  [2.868571, 0.07290757, 4.295943],
  [2.885597, 0.07317877, 4.290314],
  [2.902783, 0.07345247, 4.284534],
  [2.919893, 0.0737249, 4.278683],
  [2.937307, 0.07400215, 4.272628],
  [2.954613, 0.07427762, 4.266512],
  [2.972164, 0.07455698, 4.260208],
  [2.989641, 0.07483512, 4.253829],
  [3.006816, 0.07510848, 4.247461],
  [3.024102, 0.0753836, 4.240952],
  [3.042583, 0.07567775, 4.233883],
  [3.060053, 0.07595585, 4.227096],
  [3.077824, 0.07623875, 4.220086],
  [3.095115, 0.07651406, 4.213163],
  [3.11281, 0.07679585, 4.205976],
  [3.130429, 0.07707649, 4.198712],
  [3.14775, 0.07735247, 4.191469],
  [3.165195, 0.07763049, 4.18407],
  [3.18234, 0.07790381, 4.176698],
  [3.20043, 0.07819232, 4.16881],
  [3.217916, 0.0784713, 4.161078],
  [3.235949, 0.07875913, 4.152994],
  [3.253576, 0.0790406, 4.144983],
  [3.270987, 0.07931877, 4.136965],
  [3.288313, 0.07959574, 4.12888],
  [3.306273, 0.079883, 4.120389],
  [3.32382, 0.08016382, 4.111983],
  [3.342657, 0.08046547, 4.102839],
  [3.359478, 0.08073506, 4.094566],
  [3.377062, 0.08101705, 4.085812],
  [3.394876, 0.08130293, 4.07683],
  [3.412655, 0.08158851, 4.067752],
  [3.431526, 0.08189185, 4.057992],
  [3.449123, 0.08217499, 4.048775],
  [3.466764, 0.08245908, 4.039421],
  [3.484218, 0.08274043, 4.030054],
  [3.501721, 0.08302286, 4.020548],
  [3.519318, 0.08330705, 4.010879],
  [3.537133, 0.08359507, 4.000971],
  [3.555623, 0.08389437, 3.990563],
  [3.573328, 0.08418127, 3.980477],
  [3.591144, 0.08447032, 3.970207],
  [3.608087, 0.08474553, 3.96033],
  [3.626958, 0.08505244, 3.9492],
  [3.644345, 0.08533559, 3.938823],
  [3.662157, 0.08562606, 3.928071],
  [3.679636, 0.08591146, 3.917402],
  [3.697191, 0.08619852, 3.906564],
  [3.71507, 0.08649129, 3.895403],
  [3.732663, 0.08677984, 3.884296],
  [3.750192, 0.08706776, 3.873106],
  [3.767516, 0.08735273, 3.861928],
  [3.78611, 0.08765911, 3.849794],
  [3.803518, 0.08794644, 3.838307],
  [3.820925, 0.08823424, 3.826697],
  [3.838748, 0.08852942, 3.814679],
  [3.857255, 0.08883648, 3.80206],
  [3.87399, 0.08911467, 3.790525],
  [3.892731, 0.08942677, 3.777469],
  [3.910336, 0.08972054, 3.765068],
  [3.926703, 0.08999417, 3.753419],
  [3.944212, 0.09028748, 3.740829],
  [3.962849, 0.09060033, 3.727281],
  [3.980308, 0.09089401, 3.714452],
  [3.997003, 0.09117543, 3.702059],
  [4.015265, 0.09148394, 3.688359],
  [4.030035, 0.091734, 3.677169],
  [4.050463, 0.09208064, 3.661528],
  [4.067713, 0.09237406, 3.648172],
  [4.084831, 0.09266593, 3.634783],
  [4.102026, 0.09295983, 3.621195],
  [4.119197, 0.093254, 3.607488],
  [4.13655, 0.09355207, 3.593493],
  [4.153583, 0.09384535, 3.579616],
  [4.168056, 0.09409515, 3.567714],
  [4.186517, 0.0944146, 3.552383],
  [4.205161, 0.09473813, 3.536731],
  [4.22228, 0.09503606, 3.522206],
  [4.240459, 0.09535336, 3.506619],
  [4.257687, 0.09565493, 3.491693],
  [4.274579, 0.09595147, 3.47691],
  [4.291444, 0.09624843, 3.462002],
  [4.301944, 0.09643374, 3.452645],
  [4.32484, 0.09683904, 3.432039],
  [4.340277, 0.09711327, 3.417987],
  [4.358824, 0.0974438, 3.400933],
  [4.375385, 0.09773991, 3.385545],
  [4.393147, 0.09805854, 3.368872],
  [4.409766, 0.09835769, 3.353111],
  [4.426071, 0.09865216, 3.337495],
  [4.442694, 0.09895337, 3.321417],
  [4.459557, 0.09926002, 3.304944],
  [4.475678, 0.09955423, 3.289035],
  [4.491704, 0.09984768, 3.273069],
  [4.509483, 0.1001745, 3.255174],
  [4.525783, 0.1004752, 3.238597],
  [4.541799, 0.1007718, 3.222149],
  [4.558068, 0.1010742, 3.205276],
  [4.574216, 0.1013755, 3.188361],
  [4.59012, 0.1016735, 3.171538],
  [4.607406, 0.1019986, 3.153063],
  [4.623201, 0.1022969, 3.136009],
  [4.639396, 0.102604, 3.118351],
  [4.655592, 0.1029125, 3.100511],
  [4.671697, 0.1032205, 3.082592],
  [4.687359, 0.1035213, 3.06499],
  [4.703062, 0.1038243, 3.047167],
  [4.718816, 0.1041295, 3.029105],
  [4.734318, 0.1044312, 3.011158],
  [4.750054, 0.1047388, 2.992754],
  [4.765524, 0.1050427, 2.974478],
  [4.780847, 0.105345, 2.956197],
  [4.7966, 0.1056573, 2.937209],
  [4.812516, 0.1059744, 2.917826],
  [4.827657, 0.1062776, 2.899196],
  [4.842825, 0.1065827, 2.880347],
  [4.857854, 0.1068866, 2.861479],
  [4.87396, 0.107214, 2.841047],
  [4.888843, 0.107518, 2.82197],
  [4.903835, 0.107826, 2.802554],
  [4.918495, 0.1081287, 2.783374],
  [4.932182, 0.1084127, 2.765293],
  [4.948919, 0.108762, 2.742947],
  [4.963634, 0.1090709, 2.723081],
  [4.977826, 0.1093705, 2.703727],
  [4.992481, 0.1096816, 2.683532],
  [5.007069, 0.1099931, 2.66322],
  [5.021654, 0.1103063, 2.642695],
  [5.036441, 0.1106258, 2.621662],
  [5.050528, 0.1109321, 2.601411],
  [5.064884, 0.111246, 2.580553],
  [5.078895, 0.1115544, 2.559978],
  [5.092783, 0.1118619, 2.539367],
  [5.106894, 0.1121764, 2.5182],
  [5.120522, 0.112482, 2.497538],
  [5.13414, 0.1127894, 2.476669],
  [5.148282, 0.1131108, 2.45476],
  [5.161901, 0.1134224, 2.433426],
  [5.175286, 0.1137306, 2.41223],
  [5.189381, 0.1140576, 2.389659],
  [5.202611, 0.1143667, 2.368233],
  [5.215726, 0.1146752, 2.346759],
  [5.228838, 0.1149858, 2.325052],
  [5.242589, 0.115314, 2.302023],
  [5.255534, 0.1156253, 2.280097],
  [5.268407, 0.1159372, 2.258043],
  [5.281155, 0.1162485, 2.235953],
  [5.293899, 0.116562, 2.213619],
  [5.306581, 0.1168764, 2.191133],
  [5.318984, 0.1171863, 2.16889],
  [5.331544, 0.1175027, 2.1461],
  [5.344822, 0.11784, 2.121713],
  [5.355115, 0.1181035, 2.102591],
  [5.369364, 0.1184714, 2.075805],
  [5.37775, 0.1186897, 2.059865],
  [5.393313, 0.1190981, 2.029925],
  [5.405218, 0.1194138, 2.0067],
  [5.417224, 0.1197349, 1.982989],
  [5.42892, 0.1200506, 1.9596],
  [5.44038, 0.1203628, 1.936397],
  [5.451819, 0.1206773, 1.912947],
  [5.463214, 0.1209935, 1.889295],
  [5.474378, 0.1213062, 1.865828],
  [5.485645, 0.1216249, 1.841837],
  [5.497274, 0.1219572, 1.816748],
  [5.503503, 0.1221366, 1.803165],
  [5.519397, 0.1225991, 1.768048],
  [5.530186, 0.122917, 1.743817],
  [5.540707, 0.1232303, 1.719872],
  [5.547147, 0.1234237, 1.705056],
  [5.559341, 0.1237933, 1.676665],
  [5.572128, 0.124186, 1.646398],
  [5.582238, 0.1245004, 1.622095],
  [5.592499, 0.124823, 1.597082],
  [5.602326, 0.1251356, 1.572784],
  [5.612717, 0.1254701, 1.546715],
  [5.622462, 0.1257875, 1.521903],
  [5.632072, 0.1261043, 1.497079],
  [5.641592, 0.126422, 1.472121],
  [5.651096, 0.1267432, 1.446829],
  [5.660541, 0.1270664, 1.421307],
  [5.669735, 0.1273851, 1.396079],
  [5.678711, 0.1277004, 1.371068],
  [5.687924, 0.1280282, 1.344991],
  [5.696744, 0.1283465, 1.31962],
  [5.705397, 0.128663, 1.294331],
  [5.714094, 0.1289855, 1.268498],
  [5.722964, 0.1293194, 1.241703],
  [5.731232, 0.1296351, 1.216298],
  [5.739401, 0.1299517, 1.190773],
  [5.747479, 0.1302695, 1.165101],
  [5.756091, 0.1306136, 1.137239],
  [5.763912, 0.1309312, 1.111469],
  [5.771521, 0.1312451, 1.085949],
  [5.779161, 0.1315654, 1.059861],
  [5.786705, 0.1318869, 1.033617],
  [5.794622, 0.1322302, 1.005539],
  [5.801836, 0.1325487, 0.9794447],
  [5.80875, 0.1328592, 0.9539558],
  [5.815941, 0.133188, 0.926919],
  [5.822691, 0.1335023, 0.9010259],
  [5.829613, 0.1338307, 0.8739232],
  [5.836616, 0.1341696, 0.8459062],
  [5.843112, 0.1344904, 0.8193426],
  [5.849615, 0.134818, 0.7921692],
  [5.85593, 0.1351428, 0.7651793],
  [5.862011, 0.1354623, 0.7385973],
  [5.86808, 0.135788, 0.7114518],
  [5.874154, 0.1361214, 0.6836245],
  [5.879747, 0.1364355, 0.6573701],
  [5.885617, 0.1367729, 0.6291288],
  [5.890935, 0.1370859, 0.6028861],
  [5.89637, 0.1374138, 0.5753682],
  [5.901798, 0.1377496, 0.5471374],
  [5.906903, 0.1380741, 0.5198331],
  [5.91167, 0.138385, 0.4936349],
  [5.916514, 0.1387095, 0.4662575],
  [5.9214, 0.1390463, 0.4378063],
  [5.925947, 0.1393692, 0.4104973],
  [5.929465, 0.1396258, 0.3887755],
  [5.934824, 0.1400292, 0.3545941],
  [5.939041, 0.1403583, 0.3266684],
  [5.943099, 0.1406858, 0.2988524],
  [5.947002, 0.1410119, 0.2711289],
  [5.950734, 0.141335, 0.2436347],
  [5.954382, 0.1416629, 0.2157103],
  [5.95785, 0.1419868, 0.1880958],
  [5.961242, 0.1423167, 0.1599498],
  [5.964402, 0.1426373, 0.132576],
  [5.967374, 0.1429519, 0.1056969],
  [5.970456, 0.1432936, 0.07647577],
  [5.973339, 0.1436297, 0.04771358],
  [5.975983, 0.1439542, 0.01991996],
  [5.978524, 0.1442834, -0.008281827],
  [5.98086, 0.1446037, -0.03574783],
  [5.983159, 0.1449385, -0.0644685],
  [5.985238, 0.1452614, -0.09219038],
  [5.987205, 0.1455886, -0.120283],
  [5.989075, 0.1459239, -0.1490883],
  [5.990736, 0.1462467, -0.1768432],
  [5.992291, 0.1465763, -0.2051839],
  [5.993735, 0.1469135, -0.2341885],
  [5.994994, 0.1472413, -0.2623901],
  [5.996099, 0.147565, -0.290251],
  [5.997079, 0.1478929, -0.3184825],
  [5.997918, 0.1482208, -0.3467236],
  [5.99861, 0.1485463, -0.3747577],
  [5.999187, 0.1488883, -0.4042194],
  [5.999574, 0.1491947, -0.4306156],
  [5.99985, 0.1495228, -0.4588824],
  [5.999987, 0.1498597, -0.4879136],
  [5.999976, 0.1501935, -0.5166731],
  [5.999822, 0.15052, -0.5448101],
  [5.999522, 0.1508525, -0.5734501],
  [5.99908, 0.1511829, -0.6019183],
  [5.998488, 0.1515157, -0.6305878],
  [5.997849, 0.151808, -0.6557547],
  [5.996898, 0.1521716, -0.687065],
  [5.995893, 0.1524986, -0.7152252],
  [5.994712, 0.1528355, -0.7442204],
  [5.993422, 0.1531627, -0.772367],
  [5.991963, 0.1534958, -0.8010223],
  [5.990399, 0.1538209, -0.8289759],
  [5.988672, 0.1541506, -0.8573055],
  [5.986804, 0.15448, -0.8856035],
  [5.98489, 0.1547941, -0.912577],
  [5.982548, 0.1551527, -0.9433547],
  [5.980433, 0.1554563, -0.9694003],
  [5.977862, 0.1558041, -0.9992144],
  [5.975229, 0.15614, -1.027993],
  [5.973088, 0.1564002, -1.050274],
  [5.96958, 0.1568053, -1.084936],
  [5.9666, 0.1571314, -1.112813],
  [5.963384, 0.1574674, -1.141523],
  [5.960087, 0.1577972, -1.169669],
  [5.956554, 0.1581357, -1.198543],
  [5.952988, 0.1584638, -1.226508],
  [5.949164, 0.1588023, -1.255328],
  [5.945289, 0.1591327, -1.283427],
  [5.941272, 0.159463, -1.311498],
  [5.937141, 0.1597913, -1.339364],
  [5.932736, 0.1601299, -1.368077],
  [5.928295, 0.1604602, -1.396065],
  [5.923696, 0.1607919, -1.424126],
  [5.918979, 0.1611219, -1.452018],
  [5.914058, 0.1614562, -1.48024],
  [5.909014, 0.1617893, -1.508322],
  [5.90393, 0.1621159, -1.535826],
  [5.898704, 0.162443, -1.563331],
  [5.895283, 0.1626525, -1.580932],
  [5.88744, 0.1631207, -1.620211],
  [5.881683, 0.1634542, -1.648142],
  [5.875852, 0.163784, -1.675721],
  [5.869771, 0.16412, -1.703774],
  [5.86401, 0.1644313, -1.729729],
  [5.857596, 0.1647703, -1.757954],
  [5.851274, 0.1650974, -1.78514],
  [5.844696, 0.1654305, -1.812781],
  [5.838008, 0.1657622, -1.840264],
  [5.831113, 0.1660973, -1.867976],
  [5.824114, 0.1664307, -1.895501],
  [5.816935, 0.1667661, -1.923138],
  [5.809794, 0.1670935, -1.950065],
  [5.802334, 0.1674291, -1.977624],
  [5.794785, 0.1677627, -2.004958],
  [5.787103, 0.168096, -2.032223],
  [5.779378, 0.1684255, -2.059113],
  [5.771036, 0.1687751, -2.08759],
  [5.763067, 0.1691033, -2.114275],
  [5.754748, 0.1694404, -2.141618],
  [5.746518, 0.1697686, -2.16818],
  [5.737864, 0.1701082, -2.195608],
  [5.729288, 0.1704395, -2.22231],
  [5.721044, 0.1707533, -2.247541],
  [5.711759, 0.1711016, -2.275477],
  [5.702779, 0.1714332, -2.302026],
  [5.693565, 0.1717687, -2.328811],
  [5.68426, 0.1721027, -2.355413],
  [5.674832, 0.1724364, -2.381924],
  [5.665726, 0.1727544, -2.407129],
  [5.659462, 0.1729708, -2.424244],
  [5.645629, 0.1734421, -2.461426],
  [5.636392, 0.173752, -2.485801],
  [5.62625, 0.1740881, -2.512163],
  [5.615716, 0.1744327, -2.539121],
  [5.605532, 0.1747617, -2.564783],
  [5.595308, 0.175088, -2.590171],
  [5.58453, 0.1754279, -2.616539],
  [5.573751, 0.1757637, -2.642518],
  [5.562773, 0.1761018, -2.668592],
  [5.551857, 0.1764341, -2.694142],
  [5.540555, 0.1767743, -2.720222],
  [5.529554, 0.1771017, -2.745245],
  [5.518142, 0.1774377, -2.770847],
  [5.506878, 0.1777658, -2.795765],
  [5.495139, 0.1781041, -2.82138],
  [5.483642, 0.178432, -2.846127],
  [5.471756, 0.1787675, -2.871367],
  [5.459723, 0.1791037, -2.896573],
  [5.447567, 0.17944, -2.921702],
  [5.436255, 0.1797499, -2.944783],
  [5.423873, 0.1800859, -2.969728],
  [5.410772, 0.180438, -2.995768],
  [5.398216, 0.1807722, -3.020395],
  [5.389684, 0.1809975, -3.03695],
  [5.373552, 0.1814198, -3.067863],
  [5.360358, 0.1817615, -3.09278],
  [5.34836, 0.1820697, -3.115164],
  [5.334836, 0.182414, -3.140084],
  [5.320916, 0.1827652, -3.165403],
  [5.30723, 0.1831075, -3.189979],
  [5.294831, 0.183415, -3.211976],
  [5.280532, 0.1837668, -3.237039],
  [5.267651, 0.1840812, -3.259345],
  [5.253646, 0.1844202, -3.28331],
  [5.239252, 0.1847658, -3.307638],
  [5.22641, 0.1850718, -3.329089],
  [5.211682, 0.1854202, -3.353407],
  [5.197222, 0.1857595, -3.376989],
  [5.183932, 0.1860691, -3.398416],
  [5.169158, 0.1864108, -3.421964],
  [5.153996, 0.1867587, -3.445838],
  [5.139016, 0.1871, -3.469143],
  [5.125316, 0.18741, -3.490218],
  [5.11672, 0.1876034, -3.503324],
  [5.095648, 0.1880744, -3.535089],
  [5.080413, 0.1884121, -3.557736],
  [5.064458, 0.1887633, -3.581175],
  [5.050661, 0.189065, -3.601218],
  [5.034664, 0.1894127, -3.624204],
  [5.020438, 0.1897199, -3.644415],
  [5.003694, 0.1900791, -3.667938],
  [4.987883, 0.1904162, -3.689893],
  [4.972189, 0.1907487, -3.711437],
  [4.955935, 0.1910909, -3.733501],
  [4.94061, 0.1914116, -3.754074],
  [4.924874, 0.1917391, -3.774969],
  [4.908179, 0.1920845, -3.79689],
  [4.891724, 0.1924229, -3.818254],
  [4.876522, 0.1927338, -3.837778],
  [4.859624, 0.1930775, -3.859244],
  [4.844177, 0.19339, -3.878656],
  [4.827116, 0.1937333, -3.899867],
  [4.8107, 0.1940618, -3.920047],
  [4.794393, 0.1943865, -3.939882],
  [4.7769, 0.194733, -3.960923],
  [4.75951, 0.1950756, -3.981605],
  [4.742436, 0.1954103, -4.001686],
  [4.726924, 0.1957129, -4.019741],
  [4.708987, 0.1960611, -4.040396],
  [4.691487, 0.1963992, -4.060323],
  [4.675533, 0.196706, -4.078297],
  [4.656714, 0.1970661, -4.099269],
  [4.639379, 0.1973963, -4.11837],
  [4.623219, 0.1977028, -4.135992],
  [4.604804, 0.1980505, -4.155858],
  [4.586709, 0.1983905, -4.175159],
  [4.570144, 0.1987005, -4.192642],
  [4.551676, 0.1990447, -4.211926],
  [4.53306, 0.1993902, -4.231144],
  [4.516602, 0.1996943, -4.247954],
  [4.496973, 0.2000556, -4.267786],
  [4.479096, 0.2003833, -4.285643],
  [4.462006, 0.2006954, -4.302537],
  [4.445224, 0.2010007, -4.318956],
  [4.42453, 0.2013757, -4.338978],
  [4.407694, 0.2016797, -4.355085],
  [4.388389, 0.2020269, -4.373357],
  [4.369258, 0.2023697, -4.391254],
  [4.351932, 0.2026792, -4.407292],
  [4.3331, 0.2030143, -4.424536],
  [4.315016, 0.2033351, -4.440914],
  [4.295278, 0.203684, -4.458593],
  [4.277735, 0.203993, -4.474132],
  [4.258362, 0.2043332, -4.491106],
  [4.238898, 0.204674, -4.507965],
  [4.221449, 0.2049784, -4.522916],
  [4.200616, 0.2053408, -4.540563],
  [4.182603, 0.2056532, -4.555648],
  [4.162799, 0.2059956, -4.572049],
  [4.145251, 0.2062982, -4.586422],
  [4.125391, 0.2066397, -4.602509],
  [4.105214, 0.2069856, -4.618662],
  [4.087303, 0.2072919, -4.632839],
  [4.067361, 0.2076319, -4.648447],
  [4.047413, 0.2079712, -4.663876],
  [4.029243, 0.2082795, -4.677773],
  [4.009284, 0.2086172, -4.692862],
  [3.988959, 0.2089603, -4.708047],
  [3.971172, 0.2092598, -4.721183],
  [3.950616, 0.2096051, -4.736191],
  [3.930303, 0.2099456, -4.750842],
  [3.91204, 0.210251, -4.76386],
  [3.891751, 0.2105896, -4.778156],
  [3.871263, 0.2109307, -4.792413],
  [3.853261, 0.2112298, -4.804796],
  [3.833786, 0.2115528, -4.818037],
  [3.8157, 0.2118522, -4.830195],
  [3.792777, 0.2122309, -4.84541],
  [3.772388, 0.212567, -4.858763],
  [3.753886, 0.2128715, -4.870734],
  [3.733154, 0.2132121, -4.883986],
  [3.712567, 0.2135497, -4.896973],
  [3.694116, 0.2138518, -4.908471],
  [3.673317, 0.2141917, -4.921272],
  [3.656831, 0.2144608, -4.931299],
  [3.635716, 0.214805, -4.943988],
  [3.61263, 0.2151806, -4.957664],
  [3.593495, 0.2154915, -4.968844],
  [3.574301, 0.215803, -4.979919],
  [3.554243, 0.216128, -4.991344],
  [3.533666, 0.216461, -5.00291],
  [3.512372, 0.2168052, -5.014709],
  [3.491152, 0.2171477, -5.026302],
  [3.471885, 0.2174584, -5.036685],
  [3.452876, 0.2177646, -5.046794],
  [3.431467, 0.2181091, -5.058024],
  [3.412371, 0.2184161, -5.067899],
  [3.391336, 0.2187539, -5.078624],
  [3.370327, 0.219091, -5.089179],
  [3.351458, 0.2193935, -5.098524],
  [3.330251, 0.2197332, -5.108876],
  [3.309337, 0.220068, -5.118929],
  [3.290459, 0.2203699, -5.127872],
  [3.26932, 0.2207079, -5.137738],
  [3.250243, 0.2210126, -5.146506],
  [3.228658, 0.2213573, -5.156277],
  [3.207585, 0.2216935, -5.165659],
  [3.188804, 0.2219931, -5.173892],
  [3.166972, 0.2223412, -5.183311],
  [3.147105, 0.2226578, -5.191741],
  [3.128213, 0.2229588, -5.199631],
  [3.107136, 0.2232945, -5.208293],
  [3.085866, 0.2236332, -5.216879],
  [3.06705, 0.2239328, -5.224349],
  [3.045033, 0.2242833, -5.232938],
  [3.02624, 0.2245824, -5.240141],
  [3.005359, 0.2249147, -5.248006],
  [2.984285, 0.2252501, -5.255795],
  [2.965342, 0.2255516, -5.262672],
  [2.944583, 0.225882, -5.270069],
  [2.92345, 0.2262185, -5.277454],
  [2.904616, 0.2265184, -5.283913],
  [2.883757, 0.2268505, -5.290926],
  [2.864621, 0.2271554, -5.297236],
  [2.843783, 0.2274874, -5.30397],
  [2.822872, 0.2278207, -5.310585],
  [2.804136, 0.2281195, -5.316389],
  [2.783254, 0.2284527, -5.322722],
  [2.762266, 0.2287876, -5.328944],
  [2.743566, 0.2290863, -5.334367],
  [2.722722, 0.2294193, -5.340278],
  [2.701818, 0.2297536, -5.346061],
  [2.682971, 0.2300551, -5.351155],
  [2.662307, 0.230386, -5.356607],
  [2.643653, 0.2306849, -5.361408],
  [2.623197, 0.2310129, -5.366544],
  [2.602415, 0.2313464, -5.371622],
  [2.583799, 0.2316455, -5.376051],
  [2.563253, 0.2319758, -5.380808],
  [2.542738, 0.232306, -5.385422],
  [2.52846, 0.232536, -5.388552],
  [2.50439, 0.2329242, -5.393677],
  [2.483881, 0.2332554, -5.397896],
  [2.46337, 0.2335869, -5.401977],
  [2.445069, 0.2338831, -5.405502],
  [2.424581, 0.2342152, -5.40932],
  [2.406366, 0.2345108, -5.412598],
  [2.386086, 0.2348402, -5.41612],
  [2.365581, 0.2351739, -5.419543],
  [2.347461, 0.2354692, -5.422454],
  [2.327051, 0.2358022, -5.425601],
  [2.309031, 0.2360967, -5.428267],
  [2.288838, 0.2364273, -5.431127],
  [2.268666, 0.236758, -5.433847],
  [2.250882, 0.2370501, -5.436134],
  [2.230705, 0.2373821, -5.438602],
  [2.212949, 0.2376747, -5.440661],
  [2.192972, 0.2380044, -5.44285],
  [2.173213, 0.2383313, -5.444884],
  [2.155163, 0.2386304, -5.446629],
  [2.135341, 0.2389595, -5.448414],
  [2.115694, 0.2392864, -5.450056],
  [2.098022, 0.239581, -5.451419],
  [2.078348, 0.2399097, -5.45281],
  [2.060543, 0.2402078, -5.453957],
  [2.040978, 0.240536, -5.45509],
  [2.023614, 0.240828, -5.455985],
  [2.004053, 0.2411576, -5.456868],
  [1.986443, 0.2414551, -5.457549],
  [1.966809, 0.2417875, -5.45818],
  [1.949528, 0.2420808, -5.458625],
  [1.93039, 0.2424064, -5.458993],
  [1.911168, 0.2427342, -5.459232],
  [1.900409, 0.2429182, -5.45931],
  [1.87482, 0.2433567, -5.459325],
  [1.857901, 0.2436475, -5.459206],
  [1.83903, 0.2439728, -5.458951],
  [1.820186, 0.2442985, -5.458567],
  [1.803257, 0.244592, -5.458111],
  [1.784653, 0.2449153, -5.457487],
  [1.76784, 0.2452084, -5.456814],
  [1.749368, 0.2455313, -5.455953],
  [1.730909, 0.245855, -5.454965],
  [1.714388, 0.2461457, -5.453972],
  [1.695897, 0.2464719, -5.452736],
  [1.678794, 0.2467746, -5.451477],
  [1.661304, 0.2470852, -5.450073],
  [1.644818, 0.2473788, -5.44864],
  [1.62674, 0.2477019, -5.446948],
  [1.608751, 0.2480244, -5.445137],
  [1.592758, 0.2483122, -5.443417],
  [1.574803, 0.2486364, -5.441367],
  [1.558985, 0.2489229, -5.439452],
  [1.541268, 0.249245, -5.437187],
  [1.524271, 0.2495551, -5.434892],
  [1.508035, 0.2498524, -5.43259],
  [1.490378, 0.250177, -5.429961],
  [1.474875, 0.250463, -5.427546],
  [1.457453, 0.2507857, -5.424709],
  [1.442099, 0.2510711, -5.422101],
  [1.424871, 0.2513926, -5.419056],
  [1.407779, 0.2517129, -5.415905],
  [1.392646, 0.2519976, -5.41301],
  [1.375851, 0.2523149, -5.409676],
  [1.360564, 0.2526048, -5.406531],
  [1.343619, 0.2529276, -5.402924],
  [1.332422, 0.2531416, -5.400467],
  [1.312101, 0.2535317, -5.395862],
  [1.295538, 0.2538514, -5.391966],
  [1.28083, 0.2541364, -5.388397],
  [1.26442, 0.2544558, -5.384294],
  [1.25348, 0.2546696, -5.381486],
  [1.233909, 0.2550538, -5.376318],
  [1.21777, 0.2553723, -5.371914],
  [1.203262, 0.2556601, -5.367845],
  [1.187234, 0.2559794, -5.363225],
  [1.172945, 0.2562654, -5.358995],
  [1.158002, 0.256566, -5.354461],
  [1.142392, 0.2568817, -5.349599],
  [1.128564, 0.2571625, -5.345183],
  [1.113038, 0.2574795, -5.340103],
  [1.100928, 0.257728, -5.33605],
  [1.083671, 0.2580839, -5.330135],
  [1.070036, 0.2583666, -5.325344],
  [1.055072, 0.2586786, -5.319965],
  [1.040035, 0.2589938, -5.31443],
  [1.02669, 0.259275, -5.309407],
  [1.011922, 0.259588, -5.303727],
  [0.998682, 0.2598701, -5.298524],
  [0.9888513, 0.2600805, -5.294592],
  [0.9718986, 0.2604454, -5.287672],
  [0.9560525, 0.2607888, -5.28104],
  [0.9430587, 0.2610721, -5.275485],
  [0.9287887, 0.2613851, -5.269258],
  [0.9160581, 0.2616659, -5.26359],
  [0.9019613, 0.2619788, -5.257188],
  [0.889658, 0.2622536, -5.251493],
  [0.8758507, 0.2625637, -5.244975],
  [0.8620539, 0.2628757, -5.238329],
  [0.8498943, 0.2631524, -5.232362],
  [0.8361793, 0.2634664, -5.225501],
  [0.8244886, 0.2637359, -5.219546],
  [0.8109994, 0.2640487, -5.212546],
  [0.7978783, 0.2643552, -5.205605],
  [0.7859688, 0.2646351, -5.199189],
  [0.772963, 0.264943, -5.192055],
  [0.7614551, 0.2652172, -5.185631],
  [0.7485743, 0.2655263, -5.178309],
  [0.7372575, 0.2657997, -5.171765],
  [0.7244115, 0.2661123, -5.164204],
  [0.7131448, 0.2663884, -5.157454],
  [0.7005975, 0.2666981, -5.149806],
  [0.6896377, 0.2669707, -5.14301],
  [0.6773596, 0.2672782, -5.135264],
  [0.6652098, 0.2675849, -5.127461],
  [0.6544623, 0.2678582, -5.120441],
  [0.6424994, 0.2681649, -5.112494],
  [0.631866, 0.2684395, -5.105313],
  [0.6201346, 0.2687449, -5.097253],
  [0.6096854, 0.2690191, -5.089955],
  [0.5983472, 0.269319, -5.081904],
  [0.586853, 0.2696256, -5.0736],
  [0.5768316, 0.269895, -5.066239],
  [0.565609, 0.2701993, -5.05786],
  [0.5556231, 0.2704724, -5.050281],
  [0.5446885, 0.2707739, -5.041846],
  [0.5348315, 0.271048, -5.034117],
  [0.5240488, 0.2713505, -5.025522],
  [0.514461, 0.2716219, -5.017755],
  [0.5038064, 0.2719262, -5.008981],
  [0.494401, 0.2721972, -5.001108],
  [0.4841599, 0.272495, -4.992398],
  [0.4738989, 0.2727962, -4.983521],
  [0.4648483, 0.2730645, -4.975564],
  [0.4551799, 0.2733536, -4.966928],
  [0.4468174, 0.2736061, -4.959342],
  [0.4360993, 0.2739327, -4.94946],
  [0.4273634, 0.2742018, -4.941265],
  [0.417793, 0.2744994, -4.932144],
  [0.4091308, 0.2747715, -4.923751],
  [0.3997376, 0.2750697, -4.9145],
  [0.3914452, 0.2753356, -4.906199],
  [0.3822408, 0.2756338, -4.896836],
  [0.3731623, 0.2759312, -4.887439],
  [0.3651645, 0.2761962, -4.879025],
  [0.3562419, 0.2764949, -4.869484],
  [0.3484604, 0.2767583, -4.861025],
  [0.3396683, 0.2770592, -4.851306],
  [0.332005, 0.2773245, -4.842693],
  [0.323612, 0.2776184, -4.833103],
  [0.3161376, 0.2778831, -4.824419],
  [0.3078129, 0.2781815, -4.814583],
  [0.300652, 0.2784413, -4.805978],
  [0.2926464, 0.2787352, -4.796197],
  [0.2855108, 0.2790003, -4.787329],
  [0.2776573, 0.2792958, -4.777399],
  [0.2707145, 0.2795604, -4.768468],
  [0.2630949, 0.2798545, -4.758495],
  [0.2556176, 0.2801471, -4.748528],
  [0.2489667, 0.2804108, -4.739505],
  [0.2417474, 0.2807009, -4.729537],
  [0.2353766, 0.2809604, -4.720585],
  [0.2281976, 0.2812568, -4.710314],
  [0.2220075, 0.2815161, -4.701298],
  [0.215327, 0.2817997, -4.691391],
  [0.2093344, 0.2820579, -4.682343],
  [0.2025864, 0.2823528, -4.671966],
  [0.1968255, 0.2826084, -4.662939],
  [0.1903756, 0.2828988, -4.652644],
  [0.1847434, 0.2831564, -4.643483],
  [0.1790032, 0.2834229, -4.633973],
  [0.1724911, 0.2837302, -4.622965],
  [0.1669836, 0.2839946, -4.613463],
  [0.1611996, 0.2842768, -4.603285],
  [0.1559522, 0.2845371, -4.593867],
  [0.150789, 0.2847974, -4.58442],
  [0.1457839, 0.285054, -4.575084],
  [0.1397302, 0.2853701, -4.563543],
  [0.1348886, 0.2856278, -4.554109],
  [0.1295755, 0.2859158, -4.543529],
  [0.1249561, 0.2861709, -4.534132],
  [0.1202247, 0.2864371, -4.524303],
  [0.1153886, 0.2867145, -4.51403],
  [0.1110229, 0.2869698, -4.504549],
  [0.1067593, 0.287224, -4.49509],
  [0.1020958, 0.2875078, -4.4845],
  [0.09753442, 0.2877916, -4.473883],
  [0.09350872, 0.2880476, -4.464284],
  [0.08915782, 0.2883304, -4.453656],
  [0.08533454, 0.2885845, -4.44408],
  [0.08121872, 0.2888646, -4.43351],
  [0.07753968, 0.2891208, -4.423814],
  [0.0735538, 0.2894053, -4.413026],
  [0.07010984, 0.2896573, -4.403452],
  [0.06634974, 0.2899395, -4.392708],
  [0.06306553, 0.2901926, -4.383057],
  [0.05988407, 0.290444, -4.373448],
  [0.05675077, 0.2906981, -4.363719],
  [0.05338931, 0.2909787, -4.352962],
  [0.05022645, 0.2912508, -4.342511],
  [0.04728627, 0.2915114, -4.332482],
  [0.04452276, 0.2917638, -4.322754],
  [0.04156709, 0.2920426, -4.311996],
  [0.03897715, 0.292295, -4.302238],
  [0.03633308, 0.2925615, -4.291923],
  [0.03441501, 0.2927609, -4.284195],
  [0.03142095, 0.2930835, -4.271674],
  [0.02920032, 0.2933328, -4.261985],
  [0.0267694, 0.2936168, -4.250934],
  [0.02473259, 0.2938648, -4.241273],
  [0.02274227, 0.2941172, -4.231431],
  [0.02066565, 0.2943925, -4.22068],
  [0.01887751, 0.2946409, -4.210972],
  [0.01718402, 0.2948871, -4.201338],
  [0.01537323, 0.2951643, -4.190487],
  [0.01385069, 0.2954102, -4.18085],
  [0.01240396, 0.2956566, -4.171181],
  [0.01102161, 0.295906, -4.161395],
  [0.009595156, 0.2961802, -4.150619],
  [0.008378983, 0.2964306, -4.140776],
  [0.007139444, 0.2967053, -4.129971],
  [0.006118774, 0.29695, -4.120342],
  [0.005188465, 0.2971915, -4.110832],
  [0.004223108, 0.2974662, -4.100006],
  [0.003450394, 0.2977098, -4.090405],
  [0.002678394, 0.2979822, -4.079662],
  [0.00214386, 0.2981948, -4.071274],
  [0.001544476, 0.2984679, -4.060501],
  [0.001092434, 0.2987115, -4.050885],
  [0.000679493, 0.2989838, -4.040136],
  [0.0003936291, 0.2992266, -4.030547],
  [0.0001854897, 0.2994691, -4.020969],
  [4.506111e-5, 0.2997384, -4.01033],
  [2.384186e-7, 0.2999787, -4.000844],
  [0, 0.3, -4],
  [0, 0.3, -4],
  [0, 0.3, -4],
];

// export const initCameraMovement = [
//   [0, 0, 3],
//   [0, 0, 3.00001],
//   [0, 0, 3.000068],
//   [0, 0, 3.00018],
//   [0, 0, 3.000328],
//   [0, 0, 3.000507],
//   [0, 0, 3.000677],
//   [2.384186E-07, 0, 3.001023],
//   [4.768372E-07, 0, 3.001329],
//   [7.152557E-07, 0, 3.001708],
//   [9.536743E-07, 0, 3.001903],
//   [1.430511E-06, 0, 3.002441],
//   [2.145767E-06, 0, 3.002899],
//   [3.099442E-06, 0, 3.003458],
//   [4.291534E-06, 0, 3.004112],
//   [5.483627E-06, 0, 3.004709],
//   [7.152557E-06, 0, 3.005363],
//   [9.298325E-06, 0, 3.006059],
//   [1.144409E-05, 0, 3.006724],
//   [1.454353E-05, 0, 3.007585],
//   [1.764297E-05, 0, 3.008394],
//   [2.002716E-05, 0, 3.008906],
//   [2.622604E-05, 0, 3.010204],
//   [3.123283E-05, 0, 3.011132],
//   [3.647804E-05, 0, 3.012042],
//   [4.243851E-05, 0, 3.012977],
//   [5.030632E-05, 0, 3.014135],
//   [5.865097E-05, 0, 3.015261],
//   [6.556511E-05, 0, 3.016143],
//   [7.653236E-05, 0, 3.017445],
//   [8.845329E-05, 0, 3.018762],
//   [0.0001006126, 0, 3.020002],
//   [0.0001163483, 0, 3.021518],
//   [0.000130415, 0, 3.022778],
//   [0.0001480579, 0, 3.024278],
//   [0.0001659393, 0, 3.025702],
//   [0.0001854897, 0, 3.027163],
//   [0.0002069473, 0, 3.028696],
//   [0.0002264977, 0, 3.030011],
//   [0.0002548695, 0, 3.031846],
//   [0.0002746582, 0, 3.033059],
//   [0.0003044605, 0, 3.034804],
//   [0.0003404617, 0, 3.036802],
//   [0.0003736019, 0, 3.038549],
//   [0.0004081726, 0, 3.040296],
//   [0.0004467964, 0, 3.042159],
//   [0.0004820824, 0, 3.043789],
//   [0.0005347729, 0, 3.04612],
//   [0.0005803108, 0, 3.048052],
//   [0.0006289482, 0, 3.050025],
//   [0.0006835461, 0, 3.052144],
//   [0.0007276535, 0, 3.053804],
//   [0.0007956028, 0, 3.056258],
//   [0.0008597374, 0, 3.058481],
//   [0.0009195805, 0, 3.060484],
//   [0.000982523, 0, 3.062517],
//   [0.00106287, 0, 3.065025],
//   [0.001125574, 0, 3.066915],
//   [0.001218081, 0, 3.069607],
//   [0.001302004, 0, 3.071964],
//   [0.001400709, 0, 3.074639],
//   [0.001496077, 0, 3.077139],
//   [0.001598597, 0, 3.079741],
//   [0.001692772, 0, 3.082051],
//   [0.001788616, 0, 3.08434],
//   [0.001897097, 0, 3.086863],
//   [0.002035141, 0, 3.089965],
//   [0.002151012, 0, 3.092487],
//   [0.002226353, 0, 3.094096],
//   [0.002400875, 0, 3.097708],
//   [0.002570152, 0, 3.101094],
//   [0.002659559, 0, 3.102836],
//   [0.00283742, 0, 3.106219],
//   [0.003008366, 0, 3.10937],
//   [0.003205538, 0, 3.112892],
//   [0.003348112, 0, 3.115376],
//   [0.003549099, 0, 3.118787],
//   [0.003717661, 0, 3.121569],
//   [0.003932238, 0, 3.125027],
//   [0.004108906, 0, 3.127802],
//   [0.00436306, 0, 3.131692],
//   [0.004558325, 0, 3.134604],
//   [0.004791498, 0, 3.137999],
//   [0.005033731, 0, 3.141443],
//   [0.005272388, 0, 3.144752],
//   [0.00552845, 0, 3.148221],
//   [0.005699158, 0, 3.150488],
//   [0.006066084, 0, 3.155254],
//   [0.006338835, 0, 3.158699],
//   [0.006608963, 0, 3.162042],
//   [0.006932735, 0, 3.165957],
//   [0.007245064, 0, 3.169647],
//   [0.007557869, 0, 3.173265],
//   [0.007866621, 0, 3.176763],
//   [0.008183718, 0, 3.180284],
//   [0.008569002, 0, 3.184471],
//   [0.008909464, 0, 3.188095],
//   [0.009236336, 0, 3.191506],
//   [0.009679079, 0, 3.196034],
//   [0.01005554, 0, 3.199801],
//   [0.01042557, 0, 3.203436],
//   [0.01085854, 0, 3.207607],
//   [0.01126432, 0, 3.211442],
//   [0.01171446, 0, 3.215616],
//   [0.01221848, 0, 3.220193],
//   [0.01264834, 0, 3.224021],
//   [0.01312995, 0, 3.228234],
//   [0.01362157, 0, 3.232455],
//   [0.01405025, 0, 3.236074],
//   [0.01461411, 0, 3.24075],
//   [0.01511383, 0, 3.244817],
//   [0.01570487, 0, 3.249544],
//   [0.01618361, 0, 3.253305],
//   [0.01685071, 0, 3.258454],
//   [0.01737809, 0, 3.262452],
//   [0.01808476, 0, 3.267715],
//   [0.01858449, 0, 3.271373],
//   [0.01934361, 0, 3.276837],
//   [0.01993942, 0, 3.28105],
//   [0.02044535, 0, 3.284578],
//   [0.02133512, 0, 3.290676],
//   [0.02201223, 0, 3.29523],
//   [0.02273393, 0, 3.300008],
//   [0.02344656, 0, 3.304649],
//   [0.02423835, 0, 3.309725],
//   [0.02498627, 0, 3.314441],
//   [0.02534699, 0, 3.316689],
//   [0.02659225, 0, 3.324332],
//   [0.02741075, 0, 3.329256],
//   [0.02816439, 0, 3.333724],
//   [0.02901793, 0, 3.338712],
//   [0.02996039, 0, 3.344133],
//   [0.03085256, 0, 3.349185],
//   [0.03179932, 0, 3.354466],
//   [0.03232455, 0, 3.35736],
//   [0.03364444, 0, 3.364531],
//   [0.03461647, 0, 3.36972],
//   [0.03569174, 0, 3.375374],
//   [0.03653145, 0, 3.379728],
//   [0.03763819, 0, 3.385391],
//   [0.03879714, 0, 3.39123],
//   [0.03948259, 0, 3.394642],
//   [0.04094172, 0, 3.401803],
//   [0.0420289, 0, 3.407055],
//   [0.04319286, 0, 3.4126],
//   [0.04445863, 0, 3.418544],
//   [0.04530931, 0, 3.422489],
//   [0.04680562, 0, 3.429339],
//   [0.04811788, 0, 3.435254],
//   [0.04921389, 0, 3.440129],
//   [0.05050611, 0, 3.445807],
//   [0.05145359, 0, 3.449922],
//   [0.05298042, 0, 3.456472],
//   [0.05465555, 0, 3.463547],
//   [0.05533504, 0, 3.466385],
//   [0.05701208, 0, 3.473313],
//   [0.05881381, 0, 3.480638],
//   [0.06038141, 0, 3.486917],
//   [0.06156445, 0, 3.491601],
//   [0.06335068, 0, 3.498583],
//   [0.06487846, 0, 3.504474],
//   [0.06599855, 0, 3.508748],
//   [0.06805754, 0, 3.516505],
//   [0.06969142, 0, 3.522574],
//   [0.07138276, 0, 3.52878],
//   [0.07295704, 0, 3.534486],
//   [0.0742383, 0, 3.539082],
//   [0.07645917, 0, 3.546952],
//   [0.07823443, 0, 3.553157],
//   [0.07934189, 0, 3.55699],
//   [0.08183789, 0, 3.565528],
//   [0.08371186, 0, 3.571847],
//   [0.08558369, 0, 3.578086],
//   [0.08751869, 0, 3.58446],
//   [0.08949471, 0, 3.590892],
//   [0.0914731, 0, 3.597256],
//   [0.09343076, 0, 3.603483],
//   [0.09551835, 0, 3.610046],
//   [0.09760141, 0, 3.616521],
//   [0.09979463, 0, 3.623257],
//   [0.1011295, 0, 3.62732],
//   [0.1036389, 0, 3.634878],
//   [0.1062477, 0, 3.642633],
//   [0.1074309, 0, 3.646117],
//   [0.1108501, 0, 3.656069],
//   [0.1131563, 0, 3.662689],
//   [0.115499, 0, 3.669339],
//   [0.1176312, 0, 3.675328],
//   [0.120327, 0, 3.682819],
//   [0.1219738, 0, 3.687349],
//   [0.1250257, 0, 3.695658],
//   [0.1274157, 0, 3.702089],
//   [0.1303763, 0, 3.709965],
//   [0.1326804, 0, 3.716027],
//   [0.1354904, 0, 3.723342],
//   [0.1368172, 0, 3.726768],
//   [0.1409473, 0, 3.737315],
//   [0.1437221, 0, 3.744307],
//   [0.1463387, 0, 3.750832],
//   [0.1490941, 0, 3.757634],
//   [0.1522059, 0, 3.765233],
//   [0.1549311, 0, 3.771817],
//   [0.157485, 0, 3.77793],
//   [0.1603374, 0, 3.784693],
//   [0.1640537, 0, 3.793403],
//   [0.1659532, 0, 3.797813],
//   [0.1700311, 0, 3.807186],
//   [0.172888, 0, 3.813677],
//   [0.1760318, 0, 3.820752],
//   [0.1797633, 0, 3.829057],
//   [0.1830611, 0, 3.836316],
//   [0.1861656, 0, 3.843082],
//   [0.1894042, 0, 3.850073],
//   [0.1929998, 0, 3.857756],
//   [0.1962705, 0, 3.864674],
//   [0.2000208, 0, 3.872526],
//   [0.2032106, 0, 3.879138],
//   [0.2068229, 0, 3.886555],
//   [0.2105565, 0, 3.894143],
//   [0.2140439, 0, 3.901161],
//   [0.2178793, 0, 3.908804],
//   [0.2201133, 0, 3.913219],
//   [0.2250311, 0, 3.922848],
//   [0.2292295, 0, 3.930973],
//   [0.2328322, 0, 3.937876],
//   [0.2371178, 0, 3.946006],
//   [0.2411273, 0, 3.953535],
//   [0.2449138, 0, 3.960577],
//   [0.2472715, 0, 3.96493],
//   [0.2527359, 0, 3.974925],
//   [0.2570035, 0, 3.982642],
//   [0.2616935, 0, 3.991035],
//   [0.2649531, 0, 3.996816],
//   [0.2700961, 0, 4.00585],
//   [0.2735691, 0, 4.011893],
//   [0.2788954, 0, 4.021071],
//   [0.2834723, 0, 4.028873],
//   [0.2867048, 0, 4.034338],
//   [0.2919989, 0, 4.043208],
//   [0.2967808, 0, 4.051136],
//   [0.2998149, 0, 4.056125],
//   [0.3061802, 0, 4.066493],
//   [0.3081443, 0, 4.069665],
//   [0.3144388, 0, 4.079748],
//   [0.3207612, 0, 4.089751],
//   [0.3233471, 0, 4.093807],
//   [0.3303671, 0, 4.104716],
//   [0.3354421, 0, 4.112513],
//   [0.3405151, 0, 4.120232],
//   [0.3441837, 0, 4.125769],
//   [0.3508801, 0, 4.135781],
//   [0.356091, 0, 4.143488],
//   [0.3610959, 0, 4.150823],
//   [0.3665972, 0, 4.158809],
//   [0.3719635, 0, 4.166527],
//   [0.3771305, 0, 4.173889],
//   [0.3830051, 0, 4.182181],
//   [0.3872557, 0, 4.188129],
//   [0.3933153, 0, 4.196536],
//   [0.399668, 0, 4.205258],
//   [0.4054842, 0, 4.213163],
//   [0.4112401, 0, 4.220914],
//   [0.4160495, 0, 4.227334],
//   [0.4226842, 0, 4.236111],
//   [0.4286652, 0, 4.243943],
//   [0.4333885, 0, 4.250077],
//   [0.4400592, 0, 4.258663],
//   [0.4465203, 0, 4.266895],
//   [0.4509342, 0, 4.272472],
//   [0.4587724, 0, 4.282284],
//   [0.4649284, 0, 4.28991],
//   [0.4718709, 0, 4.298427],
//   [0.477746, 0, 4.305566],
//   [0.4838767, 0, 4.31295],
//   [0.4909792, 0, 4.321424],
//   [0.4971633, 0, 4.328731],
//   [0.5042944, 0, 4.337077],
//   [0.5108936, 0, 4.344727],
//   [0.5175748, 0, 4.352398],
//   [0.5216918, 0, 4.357091],
//   [0.5312247, 0, 4.367853],
//   [0.5376961, 0, 4.37508],
//   [0.5444217, 0, 4.382523],
//   [0.5521147, 0, 4.390954],
//   [0.5591407, 0, 4.398578],
//   [0.5652108, 0, 4.405107],
//   [0.5735991, 0, 4.414045],
//   [0.5784686, 0, 4.419188],
//   [0.5880029, 0, 4.429164],
//   [0.5940213, 0, 4.435398],
//   [0.6025257, 0, 4.444124],
//   [0.6073811, 0, 4.449064],
//   [0.6175728, 0, 4.459334],
//   [0.6247838, 0, 4.466521],
//   [0.6328378, 0, 4.474472],
//   [0.6411519, 0, 4.482595],
//   [0.6490014, 0, 4.490189],
//   [0.6554954, 0, 4.496415],
//   [0.6642063, 0, 4.50469],
//   [0.6681347, 0, 4.508393],
//   [0.6779268, 0, 4.517546],
//   [0.6875141, 0, 4.526403],
//   [0.6968081, 0, 4.534893],
//   [0.7007484, 0, 4.538464],
//   [0.7135932, 0, 4.549988],
//   [0.7220404, 0, 4.557472],
//   [0.7301319, 0, 4.564571],
//   [0.7384453, 0, 4.571795],
//   [0.7473276, 0, 4.579436],
//   [0.756104, 0, 4.586909],
//   [0.764286, 0, 4.593808],
//   [0.773123, 0, 4.601186],
//   [0.7823582, 0, 4.608817],
//   [0.7908456, 0, 4.615759],
//   [0.7985747, 0, 4.622023],
//   [0.8094933, 0, 4.630777],
//   [0.8177974, 0, 4.637364],
//   [0.8270469, 0, 4.644628],
//   [0.8366132, 0, 4.652061],
//   [0.8448381, 0, 4.658389],
//   [0.854979, 0, 4.666111],
//   [0.8649511, 0, 4.673619],
//   [0.8738282, 0, 4.680233],
//   [0.8836374, 0, 4.687467],
//   [0.891036, 0, 4.69287],
//   [0.9023809, 0, 4.70107],
//   [0.9124227, 0, 4.708243],
//   [0.9213457, 0, 4.714551],
//   [0.9323015, 0, 4.722211],
//   [0.9412742, 0, 4.728417],
//   [0.9518962, 0, 4.735684],
//   [0.9618311, 0, 4.742405],
//   [0.9719691, 0, 4.749188],
//   [0.9828935, 0, 4.756414],
//   [0.9915185, 0, 4.762058],
//   [1.003366, 0, 4.769725],
//   [1.010058, 0, 4.774012],
//   [1.024036, 0, 4.782866],
//   [1.03475, 0, 4.789561],
//   [1.042891, 0, 4.794597],
//   [1.055052, 0, 4.802035],
//   [1.066498, 0, 4.808947],
//   [1.076762, 0, 4.815072],
//   [1.086913, 0, 4.821062],
//   [1.09861, 0, 4.827882],
//   [1.104133, 0, 4.831072],
//   [1.120381, 0, 4.840345],
//   [1.132036, 0, 4.846896],
//   [1.140136, 0, 4.851398],
//   [1.153957, 0, 4.85899],
//   [1.165802, 0, 4.865405],
//   [1.171791, 0, 4.868616],
//   [1.18685, 0, 4.876596],
//   [1.199934, 0, 4.883424],
//   [1.210477, 0, 4.888852],
//   [1.222182, 0, 4.894804],
//   [1.234526, 0, 4.900997],
//   [1.246219, 0, 4.906785],
//   [1.257264, 0, 4.912181],
//   [1.270034, 0, 4.918337],
//   [1.280272, 0, 4.923208],
//   [1.292141, 0, 4.928783],
//   [1.306248, 0, 4.935313],
//   [1.318544, 0, 4.940917],
//   [1.329988, 0, 4.946063],
//   [1.343243, 0, 4.951937],
//   [1.353878, 0, 4.956583],
//   [1.367862, 0, 4.962605],
//   [1.37973, 0, 4.967639],
//   [1.391894, 0, 4.972724],
//   [1.404727, 0, 4.978009],
//   [1.41596, 0, 4.982568],
//   [1.430599, 0, 4.988417],
//   [1.442315, 0, 4.993023],
//   [1.453316, 0, 4.997287],
//   [1.468013, 0, 5.002894],
//   [1.480925, 0, 5.007734],
//   [1.494192, 0, 5.012628],
//   [1.508148, 0, 5.017685],
//   [1.513815, 0, 5.019712],
//   [1.532421, 0, 5.026267],
//   [1.545287, 0, 5.030707],
//   [1.561001, 0, 5.036027],
//   [1.573817, 0, 5.040284],
//   [1.585691, 0, 5.044163],
//   [1.601805, 0, 5.049327],
//   [1.608909, 0, 5.051567],
//   [1.627692, 0, 5.057384],
//   [1.638932, 0, 5.060791],
//   [1.655906, 0, 5.065834],
//   [1.669415, 0, 5.06976],
//   [1.683694, 0, 5.073825],
//   [1.697202, 0, 5.07759],
//   [1.707673, 0, 5.080457],
//   [1.726056, 0, 5.08538],
//   [1.732715, 0, 5.087128],
//   [1.751063, 0, 5.091851],
//   [1.768222, 0, 5.096143],
//   [1.782314, 0, 5.099579],
//   [1.797175, 0, 5.103116],
//   [1.811624, 0, 5.106469],
//   [1.825797, 0, 5.109677],
//   [1.83132, 0, 5.110906],
//   [1.852875, 0, 5.115586],
//   [1.869167, 0, 5.119002],
//   [1.883351, 0, 5.121892],
//   [1.897773, 0, 5.12475],
//   [1.913437, 0, 5.127764],
//   [1.921284, 0, 5.129237],
//   [1.94172, 0, 5.132966],
//   [1.958117, 0, 5.135842],
//   [1.968112, 0, 5.137547],
//   [1.986251, 0, 5.140542],
//   [2.003364, 0, 5.143255],
//   [2.018648, 0, 5.145586],
//   [2.02705, 0, 5.146831],
//   [2.045052, 0, 5.149409],
//   [2.064357, 0, 5.152042],
//   [2.075021, 0, 5.153438],
//   [2.093508, 0, 5.155758],
//   [2.110018, 0, 5.157727],
//   [2.125382, 0, 5.15947],
//   [2.136314, 0, 5.160658],
//   [2.15532, 0, 5.162621],
//   [2.173051, 0, 5.164337],
//   [2.188647, 0, 5.165754],
//   [2.203172, 0, 5.166995],
//   [2.220496, 0, 5.168379],
//   [2.234588, 0, 5.169425],
//   [2.251565, 0, 5.170594],
//   [2.268377, 0, 5.171653],
//   [2.280342, 0, 5.172346],
//   [2.300326, 0, 5.173392],
//   [2.31589, 0, 5.174111],
//   [2.327134, 0, 5.174579],
//   [2.348459, 0, 5.175344],
//   [2.356286, 0, 5.175587],
//   [2.381119, 0, 5.176216],
//   [2.39576, 0, 5.176487],
//   [2.406732, 0, 5.176643],
//   [2.427506, 0, 5.176826],
//   [2.445275, 0, 5.176866],
//   [2.452103, 0, 5.176852],
//   [2.478283, 0, 5.176655],
//   [2.49397, 0, 5.176426],
//   [2.51175, 0, 5.176065],
//   [2.528126, 0, 5.17564],
//   [2.54249, 0, 5.175192],
//   [2.559014, 0, 5.174592],
//   [2.577337, 0, 5.17382],
//   [2.595601, 0, 5.172937],
//   [2.612945, 0, 5.171996],
//   [2.629386, 0, 5.171012],
//   [2.642546, 0, 5.170158],
//   [2.65387, 0, 5.169378],
//   [2.676818, 0, 5.167667],
//   [2.69464, 0, 5.166217],
//   [2.708518, 0, 5.165014],
//   [2.729915, 0, 5.163035],
//   [2.745312, 0, 5.161517],
//   [2.764683, 0, 5.159496],
//   [2.781325, 0, 5.15766],
//   [2.799716, 0, 5.155523],
//   [2.813886, 0, 5.153801],
//   [2.833994, 0, 5.151244],
//   [2.851391, 0, 5.148922],
//   [2.865653, 0, 5.146944],
//   [2.884894, 0, 5.14417],
//   [2.892941, 0, 5.142972],
//   [2.913846, 0, 5.139762],
//   [2.935644, 0, 5.13626],
//   [2.95493, 0, 5.133031],
//   [2.971951, 0, 5.130077],
//   [2.989712, 0, 5.126893],
//   [3.006586, 0, 5.12377],
//   [3.02408, 0, 5.120432],
//   [3.042723, 0, 5.116763],
//   [3.058852, 0, 5.113494],
//   [3.076727, 0, 5.10977],
//   [3.090027, 0, 5.10693],
//   [3.112924, 0, 5.101901],
//   [3.130094, 0, 5.098015],
//   [3.147512, 0, 5.09397],
//   [3.153531, 0, 5.092549],
//   [3.182821, 0, 5.085457],
//   [3.199877, 0, 5.081193],
//   [3.218212, 0, 5.0765],
//   [3.235404, 0, 5.071995],
//   [3.249616, 0, 5.068194],
//   [3.267301, 0, 5.063369],
//   [3.287441, 0, 5.057744],
//   [3.300518, 0, 5.054016],
//   [3.323501, 0, 5.047322],
//   [3.341474, 0, 5.04196],
//   [3.356041, 0, 5.037531],
//   [3.377002, 0, 5.03103],
//   [3.387385, 0, 5.027752],
//   [3.413041, 0, 5.019492],
//   [3.4295, 0, 5.014071],
//   [3.448129, 0, 5.007819],
//   [3.467033, 0, 5.00135],
//   [3.484802, 0, 4.995154],
//   [3.500621, 0, 4.989542],
//   [3.518564, 0, 4.983068],
//   [3.537764, 0, 4.976013],
//   [3.555061, 0, 4.969543],
//   [3.571582, 0, 4.963261],
//   [3.591214, 0, 4.955667],
//   [3.607517, 0, 4.949253],
//   [3.627084, 0, 4.941427],
//   [3.643672, 0, 4.93468],
//   [3.661704, 0, 4.92723],
//   [3.679268, 0, 4.919858],
//   [3.697076, 0, 4.912263],
//   [3.714745, 0, 4.90461],
//   [3.731973, 0, 4.897033],
//   [3.750166, 0, 4.888909],
//   [3.76801, 0, 4.880817],
//   [3.786753, 0, 4.872186],
//   [3.79181, 0, 4.869834],
//   [3.822044, 0, 4.855562],
//   [3.839193, 0, 4.847306],
//   [3.856925, 0, 4.838648],
//   [3.874896, 0, 4.829745],
//   [3.887603, 0, 4.823371],
//   [3.909281, 0, 4.812349],
//   [3.91654, 0, 4.808615],
//   [3.941164, 0, 4.795788],
//   [3.958175, 0, 4.786781],
//   [3.979544, 0, 4.775296],
//   [3.9973, 0, 4.765609],
//   [4.013466, 0, 4.756672],
//   [4.032022, 0, 4.746279],
//   [4.042899, 0, 4.740116],
//   [4.066324, 0, 4.726675],
//   [4.070368, 0, 4.724329],
//   [4.099268, 0, 4.707365],
//   [4.1094, 0, 4.70133],
//   [4.135229, 0, 4.685739],
//   [4.153969, 0, 4.67424],
//   [4.167242, 0, 4.665998],
//   [4.186676, 0, 4.653787],
//   [4.206289, 0, 4.641285],
//   [4.222916, 0, 4.630547],
//   [4.239707, 0, 4.61957],
//   [4.255516, 0, 4.609113],
//   [4.27342, 0, 4.597125],
//   [4.289782, 0, 4.586035],
//   [4.308175, 0, 4.57341],
//   [4.325281, 0, 4.56152],
//   [4.341166, 0, 4.550346],
//   [4.358023, 0, 4.53835],
//   [4.373354, 0, 4.527315],
//   [4.392024, 0, 4.513713],
//   [4.404765, 0, 4.504325],
//   [4.425658, 0, 4.488747],
//   [4.442936, 0, 4.475689],
//   [4.459686, 0, 4.462877],
//   [4.475271, 0, 4.450818],
//   [4.492861, 0, 4.437048],
//   [4.508566, 0, 4.424606],
//   [4.52515, 0, 4.411319],
//   [4.536657, 0, 4.402006],
//   [4.558034, 0, 4.384503],
//   [4.5738, 0, 4.371423],
//   [4.586479, 0, 4.360797],
//   [4.605051, 0, 4.345058],
//   [4.623129, 0, 4.329537],
//   [4.639584, 0, 4.315234],
//   [4.654712, 0, 4.301935],
//   [4.667705, 0, 4.290397],
//   [4.683364, 0, 4.276348],
//   [4.700311, 0, 4.260965],
//   [4.717866, 0, 4.244829],
//   [4.733648, 0, 4.230148],
//   [4.750545, 0, 4.214242],
//   [4.758678, 0, 4.206515],
//   [4.781218, 0, 4.184863],
//   [4.796567, 0, 4.169909],
//   [4.812596, 0, 4.154114],
//   [4.827563, 0, 4.139195],
//   [4.842535, 0, 4.124105],
//   [4.853438, 0, 4.113008],
//   [4.873777, 0, 4.092067],
//   [4.885858, 0, 4.079476],
//   [4.904008, 0, 4.06034],
//   [4.917091, 0, 4.046382],
//   [4.929044, 0, 4.033507],
//   [4.948025, 0, 4.012818],
//   [4.96337, 0, 3.995869],
//   [4.975276, 0, 3.982578],
//   [4.992209, 0, 3.96346],
//   [5.002691, 0, 3.951498],
//   [5.021311, 0, 3.930002],
//   [5.03556, 0, 3.913335],
//   [5.049106, 0, 3.897311],
//   [5.063443, 0, 3.880157],
//   [5.078426, 0, 3.862016],
//   [5.090656, 0, 3.847044],
//   [5.106739, 0, 3.827118],
//   [5.119522, 0, 3.81109],
//   [5.133547, 0, 3.793303],
//   [5.14885, 0, 3.773651],
//   [5.160995, 0, 3.75787],
//   [5.171114, 0, 3.744596],
//   [5.189367, 0, 3.72035],
//   [5.202958, 0, 3.70204],
//   [5.210683, 0, 3.691534],
//   [5.226015, 0, 3.670465],
//   [5.241817, 0, 3.648438],
//   [5.254813, 0, 3.630085],
//   [5.26728, 0, 3.612266],
//   [5.281341, 0, 3.591917],
//   [5.293205, 0, 3.574531],
//   [5.304977, 0, 3.557085],
//   [5.319604, 0, 3.535126],
//   [5.329793, 0, 3.519641],
//   [5.343427, 0, 3.498674],
//   [5.356795, 0, 3.477832],
//   [5.366363, 0, 3.462739],
//   [5.381101, 0, 3.439193],
//   [5.392518, 0, 3.420699],
//   [5.405385, 0, 3.399586],
//   [5.417251, 0, 3.379851],
//   [5.428494, 0, 3.360912],
//   [5.440057, 0, 3.341185],
//   [5.45194, 0, 3.320641],
//   [5.458499, 0, 3.309179],
//   [5.474306, 0, 3.281195],
//   [5.484571, 0, 3.262738],
//   [5.496404, 0, 3.24118],
//   [5.508342, 0, 3.219106],
//   [5.51909, 0, 3.198949],
//   [5.525701, 0, 3.186415],
//   [5.537555, 0, 3.163667],
//   [5.547726, 0, 3.143867],
//   [5.559845, 0, 3.119916],
//   [5.5706, 0, 3.098329],
//   [5.58247, 0, 3.074122],
//   [5.591875, 0, 3.054652],
//   [5.600345, 0, 3.036887],
//   [5.612703, 0, 3.010563],
//   [5.622285, 0, 2.989813],
//   [5.630775, 0, 2.971166],
//   [5.641745, 0, 2.946702],
//   [5.648832, 0, 2.930661],
//   [5.659865, 0, 2.905316],
//   [5.670095, 0, 2.88139],
//   [5.672857, 0, 2.874858],
//   [5.683513, 0, 2.849355],
//   [5.693543, 0, 2.824897],
//   [5.704191, 0, 2.79843],
//   [5.713327, 0, 2.77529],
//   [5.723141, 0, 2.749967],
//   [5.731597, 0, 2.727739],
//   [5.739879, 0, 2.705589],
//   [5.743943, 0, 2.694574],
//   [5.751896, 0, 2.672739],
//   [5.760561, 0, 2.648501],
//   [5.771372, 0, 2.617571],
//   [5.774703, 0, 2.607883],
//   [5.783216, 0, 2.582753],
//   [5.79232, 0, 2.555279],
//   [5.801227, 0, 2.527755],
//   [5.808657, 0, 2.504277],
//   [5.814535, 0, 2.485355],
//   [5.822767, 0, 2.458298],
//   [5.829871, 0, 2.434404],
//   [5.836291, 0, 2.412349],
//   [5.843038, 0, 2.388663],
//   [5.849621, 0, 2.365023],
//   [5.85581, 0, 2.342292],
//   [5.860878, 0, 2.323288],
//   [5.867927, 0, 2.296243],
//   [5.873483, 0, 2.274382],
//   [5.879885, 0, 2.248557],
//   [5.885513, 0, 2.225249],
//   [5.889952, 0, 2.206441],
//   [5.896263, 0, 2.179005],
//   [5.899131, 0, 2.166248],
//   [5.906445, 0, 2.132839],
//   [5.911817, 0, 2.107426],
//   [5.914716, 0, 2.093374],
//   [5.920945, 0, 2.062329],
//   [5.925963, 0, 2.036388],
//   [5.930428, 0, 2.012533],
//   [5.934515, 0, 1.989998],
//   [5.937237, 0, 1.974584],
//   [5.943016, 0, 1.940689],
//   [5.946924, 0, 1.91676],
//   [5.949997, 0, 1.897302],
//   [5.954059, 0, 1.870619],
//   [5.957743, 0, 1.845359],
//   [5.961004, 0, 1.822051],
//   [5.964242, 0, 1.797901],
//   [5.967588, 0, 1.77175],
//   [5.970444, 0, 1.748338],
//   [5.972974, 0, 1.726604],
//   [5.975371, 0, 1.705053],
//   [5.978518, 0, 1.675069],
//   [5.980733, 0, 1.652622],
//   [5.983163, 0, 1.626436],
//   [5.985089, 0, 1.604298],
//   [5.987241, 0, 1.57779],
//   [5.988724, 0, 1.558187],
//   [5.990296, 0, 1.53591],
//   [5.992299, 0, 1.504746],
//   [5.993645, 0, 1.481429],
//   [5.994993, 0, 1.455442],
//   [5.995511, 0, 1.444532],
//   [5.996705, 0, 1.416687],
//   [5.997828, 0, 1.385361],
//   [5.998633, 0, 1.357385],
//   [5.999177, 0, 1.333287],
//   [5.999521, 0, 1.313604],
//   [5.999833, 0, 1.287581],
//   [5.999986, 0, 1.261113],
//   [5.999981, 0, 1.237358],
//   [5.999827, 0, 1.211856],
//   [5.999555, 0, 1.188721],
//   [5.999064, 0, 1.161164],
//   [5.998531, 0, 1.138723],
//   [5.997794, 0, 1.113588],
//   [5.997014, 0, 1.091315],
//   [5.995908, 0, 1.064257],
//   [5.994837, 0, 1.041381],
//   [5.993553, 0, 1.016896],
//   [5.991982, 0, 0.990066],
//   [5.990346, 0, 0.9648244],
//   [5.988781, 0, 0.942602],
//   [5.987804, 0, 0.9295148],
//   [5.984993, 0, 0.894556],
//   [5.982862, 0, 0.8702147],
//   [5.980927, 0, 0.8494011],
//   [5.977893, 0, 0.8188043],
//   [5.975122, 0, 0.7926688],
//   [5.972357, 0, 0.7680018],
//   [5.969483, 0, 0.7436633],
//   [5.966517, 0, 0.7197318],
//   [5.96346, 0, 0.6961637],
//   [5.960248, 0, 0.6724558],
//   [5.957049, 0, 0.6497978],
//   [5.953232, 0, 0.6238526],
//   [5.949191, 0, 0.5975385],
//   [5.94529, 0, 0.5731319],
//   [5.941519, 0, 0.5503643],
//   [5.93714, 0, 0.5248588],
//   [5.932897, 0, 0.5009935],
//   [5.929243, 0, 0.4810607],
//   [5.924883, 0, 0.4579529],
//   [5.919477, 0, 0.4302481],
//   [5.915275, 0, 0.4093609],
//   [5.909619, 0, 0.382087],
//   [5.904053, 0, 0.3560913],
//   [5.899475, 0, 0.3352916],
//   [5.894397, 0, 0.3127955],
//   [5.887749, 0, 0.2841767],
//   [5.882376, 0, 0.2616849],
//   [5.8759, 0, 0.2352868],
//   [5.873078, 0, 0.22401],
//   [5.864274, 0, 0.189649],
//   [5.857787, 0, 0.1650777],
//   [5.852127, 0, 0.1441214],
//   [5.844848, 0, 0.117785],
//   [5.838323, 0, 0.09473085],
//   [5.831072, 0, 0.06968838],
//   [5.824364, 0, 0.04702735],
//   [5.816832, 0, 0.02212751],
//   [5.81181, 0, 0.005832672],
//   [5.802128, 0, -0.02493393],
//   [5.796745, 0, -0.04168582],
//   [5.787406, 0, -0.07018673],
//   [5.779076, 0, -0.09503961],
//   [5.771751, 0, -0.1164751],
//   [5.76303, 0, -0.1415086],
//   [5.757121, 0, -0.1581802],
//   [5.746733, 0, -0.1869608],
//   [5.738223, 0, -0.2100496],
//   [5.729645, 0, -0.2329053],
//   [5.721101, 0, -0.2552713],
//   [5.7137, 0, -0.274339],
//   [5.702747, 0, -0.3020508],
//   [5.696166, 0, -0.3184268],
//   [5.684209, 0, -0.3476695],
//   [5.675509, 0, -0.3685554],
//   [5.665617, 0, -0.3919162],
//   [5.656734, 0, -0.4125562],
//   [5.6477, 0, -0.4332372],
//   [5.637758, 0, -0.4556428],
//   [5.630237, 0, -0.4723573],
//   [5.616405, 0, -0.5025835],
//   [5.606145, 0, -0.5245937],
//   [5.596362, 0, -0.5452688],
//   [5.584412, 0, -0.5701255],
//   [5.573716, 0, -0.5920136],
//   [5.565842, 0, -0.607919],
//   [5.55305, 0, -0.6333946],
//   [5.542503, 0, -0.6540675],
//   [5.529467, 0, -0.6792262],
//   [5.518259, 0, -0.7005186],
//   [5.507285, 0, -0.7210741],
//   [5.497172, 0, -0.739769],
//   [5.485104, 0, -0.761775],
//   [5.473805, 0, -0.7820897],
//   [5.460796, 0, -0.8051445],
//   [5.449986, 0, -0.8240371],
//   [5.43707, 0, -0.8463047],
//   [5.42492, 0, -0.8669574],
//   [5.413409, 0, -0.8862685],
//   [5.400046, 0, -0.9083817],
//   [5.386714, 0, -0.9301302],
//   [5.378462, 0, -0.9434364],
//   [5.360657, 0, -0.9717579],
//   [5.346879, 0, -0.9933202],
//   [5.334977, 0, -1.011704],
//   [5.32084, 0, -1.033257],
//   [5.307419, 0, -1.053442],
//   [5.296687, 0, -1.069392],
//   [5.281089, 0, -1.092282],
//   [5.267511, 0, -1.111932],
//   [5.253105, 0, -1.132509],
//   [5.240149, 0, -1.150779],
//   [5.227844, 0, -1.167932],
//   [5.211259, 0, -1.190748],
//   [5.201407, 0, -1.204141],
//   [5.184586, 0, -1.226739],
//   [5.170461, 0, -1.245457],
//   [5.154541, 0, -1.266277],
//   [5.138914, 0, -1.286441],
//   [5.125067, 0, -1.304083],
//   [5.109483, 0, -1.323692],
//   [5.095364, 0, -1.341238],
//   [5.08203, 0, -1.357621],
//   [5.064516, 0, -1.378866],
//   [5.048998, 0, -1.397439],
//   [5.033892, 0, -1.415296],
//   [5.018305, 0, -1.433494],
//   [5.002902, 0, -1.451257],
//   [4.990675, 0, -1.465203],
//   [4.971688, 0, -1.486598],
//   [4.957454, 0, -1.502427],
//   [4.943173, 0, -1.518136],
//   [4.930458, 0, -1.531977],
//   [4.910419, 0, -1.553518],
//   [4.893835, 0, -1.571098],
//   [4.878856, 0, -1.586788],
//   [4.860923, 0, -1.605339],
//   [4.843189, 0, -1.623443],
//   [4.826638, 0, -1.640123],
//   [4.819979, 0, -1.646776],
//   [4.79879, 0, -1.667731],
//   [4.777215, 0, -1.688735],
//   [4.760126, 0, -1.705135],
//   [4.743587, 0, -1.720815],
//   [4.726722, 0, -1.736612],
//   [4.708862, 0, -1.753131],
//   [4.692094, 0, -1.768447],
//   [4.676373, 0, -1.782642],
//   [4.658682, 0, -1.798421],
//   [4.639291, 0, -1.81549],
//   [4.624367, 0, -1.828467],
//   [4.609232, 0, -1.841487],
//   [4.589664, 0, -1.858113],
//   [4.568645, 0, -1.875716],
//   [4.554253, 0, -1.887619],
//   [4.534746, 0, -1.903558],
//   [4.51778, 0, -1.917244],
//   [4.500862, 0, -1.930728],
//   [4.480818, 0, -1.946495],
//   [4.463116, 0, -1.960235],
//   [4.44291, 0, -1.975709],
//   [4.429219, 0, -1.986069],
//   [4.409454, 0, -2.00085],
//   [4.392469, 0, -2.013386],
//   [4.370491, 0, -2.029386],
//   [4.353386, 0, -2.041666],
//   [4.335013, 0, -2.054689],
//   [4.314445, 0, -2.069069],
//   [4.294926, 0, -2.08252],
//   [4.280798, 0, -2.092141],
//   [4.260155, 0, -2.106022],
//   [4.242539, 0, -2.117706],
//   [4.2221, 0, -2.131076],
//   [4.200762, 0, -2.144826],
//   [4.183272, 0, -2.155938],
//   [4.164513, 0, -2.1677],
//   [4.144722, 0, -2.179934],
//   [4.126211, 0, -2.191216],
//   [4.106845, 0, -2.202856],
//   [4.087464, 0, -2.214339],
//   [4.068426, 0, -2.225456],
//   [4.047349, 0, -2.237582],
//   [4.031361, 0, -2.246651],
//   [4.010242, 0, -2.258463],
//   [3.994854, 0, -2.266952],
//   [3.971391, 0, -2.279701],
//   [3.950235, 0, -2.291],
//   [3.93137, 0, -2.30092],
//   [3.914275, 0, -2.309782],
//   [3.893017, 0, -2.320637],
//   [3.872085, 0, -2.331146],
//   [3.853588, 0, -2.340287],
//   [3.834397, 0, -2.349626],
//   [3.814427, 0, -2.359191],
//   [3.793105, 0, -2.36923],
//   [3.774874, 0, -2.377672],
//   [3.752549, 0, -2.387836],
//   [3.734484, 0, -2.395921],
//   [3.712424, 0, -2.405622],
//   [3.695428, 0, -2.412971],
//   [3.673205, 0, -2.422415],
//   [3.652754, 0, -2.430944],
//   [3.641838, 0, -2.435432],
//   [3.612595, 0, -2.447236],
//   [3.593618, 0, -2.454728],
//   [3.57178, 0, -2.463186],
//   [3.561136, 0, -2.467244],
//   [3.533927, 0, -2.477434],
//   [3.513085, 0, -2.485058],
//   [3.49137, 0, -2.492835],
//   [3.47903, 0, -2.497179],
//   [3.457228, 0, -2.504722],
//   [3.439745, 0, -2.510648],
//   [3.419182, 0, -2.517481],
//   [3.390247, 0, -2.526843],
//   [3.378925, 0, -2.530425],
//   [3.349547, 0, -2.539515],
//   [3.329688, 0, -2.545489],
//   [3.310191, 0, -2.551221],
//   [3.290579, 0, -2.556855],
//   [3.268257, 0, -2.563106],
//   [3.249393, 0, -2.568255],
//   [3.228299, 0, -2.573869],
//   [3.207283, 0, -2.579312],
//   [3.188798, 0, -2.583974],
//   [3.172343, 0, -2.588027],
//   [3.148414, 0, -2.593758],
//   [3.127123, 0, -2.598694],
//   [3.112217, 0, -2.60206],
//   [3.089336, 0, -2.60708],
//   [3.066566, 0, -2.6119],
//   [3.048328, 0, -2.615637],
//   [3.029135, 0, -2.61945],
//   [3.012143, 0, -2.622721],
//   [2.984527, 0, -2.627833],
//   [2.965058, 0, -2.631285],
//   [2.944589, 0, -2.634778],
//   [2.930574, 0, -2.637089],
//   [2.913509, 0, -2.639815],
//   [2.891788, 0, -2.643145],
//   [2.875769, 0, -2.645501],
//   [2.844196, 0, -2.649895],
//   [2.822566, 0, -2.652714],
//   [2.802836, 0, -2.65515],
//   [2.784679, 0, -2.657279],
//   [2.765397, 0, -2.659419],
//   [2.74503, 0, -2.661546],
//   [2.724492, 0, -2.663551],
//   [2.704091, 0, -2.665405],
//   [2.682225, 0, -2.667238],
//   [2.664373, 0, -2.668617],
//   [2.64401, 0, -2.670061],
//   [2.625103, 0, -2.671277],
//   [2.603247, 0, -2.672535],
//   [2.584031, 0, -2.673509],
//   [2.564513, 0, -2.674371],
//   [2.544746, 0, -2.675116],
//   [2.524686, 0, -2.675737],
//   [2.505904, 0, -2.676196],
//   [2.485356, 0, -2.676562],
//   [2.464983, 0, -2.676784],
//   [2.446528, 0, -2.676864],
//   [2.42531, 0, -2.676814],
//   [2.408181, 0, -2.676661],
//   [2.385865, 0, -2.676312],
//   [2.370288, 0, -2.675968],
//   [2.34796, 0, -2.675329],
//   [2.327453, 0, -2.674591],
//   [2.311246, 0, -2.673906],
//   [2.289095, 0, -2.672822],
//   [2.273565, 0, -2.67196],
//   [2.251129, 0, -2.670567],
//   [2.231303, 0, -2.669188],
//   [2.212633, 0, -2.667764],
//   [2.195789, 0, -2.666373],
//   [2.173673, 0, -2.664396],
//   [2.154127, 0, -2.662503],
//   [2.137986, 0, -2.660836],
//   [2.126769, 0, -2.659623],
//   [2.100384, 0, -2.65659],
//   [2.080516, 0, -2.654141],
//   [2.06674, 0, -2.652358],
//   [2.041571, 0, -2.64892],
//   [2.022743, 0, -2.646196],
//   [2.004889, 0, -2.643493],
//   [1.985478, 0, -2.640417],
//   [1.971247, 0, -2.638073],
//   [1.958351, 0, -2.635883],
//   [1.931105, 0, -2.631049],
//   [1.914145, 0, -2.627897],
//   [1.894401, 0, -2.624089],
//   [1.875093, 0, -2.620219],
//   [1.857702, 0, -2.616609],
//   [1.840234, 0, -2.612864],
//   [1.821402, 0, -2.608691],
//   [1.803067, 0, -2.604493],
//   [1.784567, 0, -2.600121],
//   [1.77194, 0, -2.597058],
//   [1.750467, 0, -2.5917],
//   [1.730919, 0, -2.586658],
//   [1.7161, 0, -2.582731],
//   [1.698778, 0, -2.578026],
//   [1.679731, 0, -2.572705],
//   [1.662672, 0, -2.56781],
//   [1.646397, 0, -2.563024],
//   [1.627673, 0, -2.557378],
//   [1.609322, 0, -2.551697],
//   [1.592687, 0, -2.546419],
//   [1.584471, 0, -2.543767],
//   [1.557809, 0, -2.534956],
//   [1.542412, 0, -2.529721],
//   [1.524599, 0, -2.523531],
//   [1.508932, 0, -2.517967],
//   [1.497896, 0, -2.513979],
//   [1.477545, 0, -2.506475],
//   [1.459161, 0, -2.499529],
//   [1.448347, 0, -2.495369],
//   [1.424772, 0, -2.486102],
//   [1.409956, 0, -2.480139],
//   [1.393354, 0, -2.47333],
//   [1.375272, 0, -2.465757],
//   [1.360769, 0, -2.459564],
//   [1.343718, 0, -2.452145],
//   [1.329822, 0, -2.445989],
//   [1.311525, 0, -2.437728],
//   [1.297158, 0, -2.431118],
//   [1.280432, 0, -2.423284],
//   [1.265833, 0, -2.416322],
//   [1.25033, 0, -2.408801],
//   [1.23437, 0, -2.400919],
//   [1.219153, 0, -2.393272],
//   [1.202676, 0, -2.384842],
//   [1.193052, 0, -2.379845],
//   [1.17434, 0, -2.369976],
//   [1.158183, 0, -2.36129],
//   [1.142511, 0, -2.352712],
//   [1.127235, 0, -2.344208],
//   [1.113832, 0, -2.336628],
//   [1.099005, 0, -2.328111],
//   [1.083856, 0, -2.319265],
//   [1.068725, 0, -2.310282],
//   [1.060608, 0, -2.305402],
//   [1.041736, 0, -2.293885],
//   [1.026448, 0, -2.28438],
//   [1.016993, 0, -2.278422],
//   [0.9987736, 0, -2.266766],
//   [0.9852757, 0, -2.257979],
//   [0.9727688, 0, -2.249721],
//   [0.9569423, 0, -2.239107],
//   [0.9435635, 0, -2.22999],
//   [0.9304998, 0, -2.220958],
//   [0.9159889, 0, -2.210772],
//   [0.9062438, 0, -2.203839],
//   [0.8884668, 0, -2.190999],
//   [0.8773551, 0, -2.182843],
//   [0.8633177, 0, -2.172395],
//   [0.8492475, 0, -2.161758],
//   [0.8400786, 0, -2.154735],
//   [0.8234563, 0, -2.141818],
//   [0.8112512, 0, -2.132178],
//   [0.7993946, 0, -2.122684],
//   [0.7854433, 0, -2.111348],
//   [0.7736337, 0, -2.10161],
//   [0.7614722, 0, -2.091443],
//   [0.7487423, 0, -2.080647],
//   [0.7373438, 0, -2.070842],
//   [0.7274723, 0, -2.062245],
//   [0.713511, 0, -2.049915],
//   [0.7023001, 0, -2.039865],
//   [0.6885209, 0, -2.027328],
//   [0.6794157, 0, -2.018929],
//   [0.6685429, 0, -2.008777],
//   [0.6568754, 0, -1.997732],
//   [0.642935, 0, -1.984327],
//   [0.6322365, 0, -1.973882],
//   [0.6218088, 0, -1.963564],
//   [0.6094933, 0, -1.951204],
//   [0.5985599, 0, -1.940067],
//   [0.5896449, 0, -1.93087],
//   [0.5790863, 0, -1.919839],
//   [0.5670934, 0, -1.907122],
//   [0.5564022, 0, -1.895615],
//   [0.5462093, 0, -1.88449],
//   [0.5350447, 0, -1.872127],
//   [0.5254092, 0, -1.861305],
//   [0.5155718, 0, -1.850106],
//   [0.5036709, 0, -1.836351],
//   [0.4959366, 0, -1.827286],
//   [0.4849968, 0, -1.814292],
//   [0.473841, 0, -1.800828],
//   [0.4643254, 0, -1.789167],
//   [0.4552596, 0, -1.777902],
//   [0.4488263, 0, -1.769814],
//   [0.4363701, 0, -1.753926],
//   [0.4275322, 0, -1.742465],
//   [0.4173396, 0, -1.729049],
//   [0.4094899, 0, -1.718565],
//   [0.3997445, 0, -1.705363],
//   [0.3913331, 0, -1.693796],
//   [0.3827729, 0, -1.681856],
//   [0.3748164, 0, -1.6706],
//   [0.3653727, 0, -1.657038],
//   [0.3565927, 0, -1.644226],
//   [0.3486342, 0, -1.632437],
//   [0.3399739, 0, -1.619412],
//   [0.3324265, 0, -1.607889],
//   [0.3234851, 0, -1.594023],
//   [0.3162785, 0, -1.582672],
//   [0.3103919, 0, -1.573281],
//   [0.2999909, 0, -1.556414],
//   [0.2937942, 0, -1.546194],
//   [0.2877634, 0, -1.53612],
//   [0.2794843, 0, -1.52208],
//   [0.2706397, 0, -1.506799],
//   [0.2637386, 0, -1.494667],
//   [0.2564282, 0, -1.481606],
//   [0.2492554, 0, -1.468574],
//   [0.2418206, 0, -1.45483],
//   [0.2355742, 0, -1.443088],
//   [0.2286878, 0, -1.42993],
//   [0.2221797, 0, -1.41728],
//   [0.215873, 0, -1.404816],
//   [0.210026, 0, -1.39307],
//   [0.2027097, 0, -1.378104],
//   [0.1975322, 0, -1.367325],
//   [0.1904767, 0, -1.352374],
//   [0.185364, 0, -1.341342],
//   [0.1784756, 0, -1.326202],
//   [0.1726305, 0, -1.313095],
//   [0.1673203, 0, -1.300969],
//   [0.1616349, 0, -1.287746],
//   [0.1567073, 0, -1.276074],
//   [0.1502988, 0, -1.260586],
//   [0.1451609, 0, -1.247903],
//   [0.1401589, 0, -1.235316],
//   [0.1350448, 0, -1.222189],
//   [0.1298742, 0, -1.208636],
//   [0.124795, 0, -1.195035],
//   [0.1205416, 0, -1.183411],
//   [0.115222, 0, -1.168556],
//   [0.1111615, 0, -1.156967],
//   [0.107013, 0, -1.144888],
//   [0.1018925, 0, -1.129628],
//   [0.09854126, 0, -1.119418],
//   [0.09368849, 0, -1.104297],
//   [0.08987308, 0, -1.092114],
//   [0.08563304, 0, -1.07825],
//   [0.08149743, 0, -1.064372],
//   [0.07768226, 0, -1.051235],
//   [0.07404757, 0, -1.0384],
//   [0.07008862, 0, -1.024039],
//   [0.0669179, 0, -1.012227],
//   [0.0633533, 0, -0.9985934],
//   [0.05990601, 0, -0.9850227],
//   [0.05666041, 0, -0.9718689],
//   [0.05409169, 0, -0.9611789],
//   [0.05037427, 0, -0.9452319],
//   [0.04748511, 0, -0.9324116],
//   [0.04555249, 0, -0.9236106],
//   [0.04171586, 0, -0.9055501],
//   [0.03879595, 0, -0.8912243],
//   [0.03694963, 0, -0.8818784],
//   [0.0343132, 0, -0.8681093],
//   [0.03156233, 0, -0.853152],
//   [0.02926087, 0, -0.8401177],
//   [0.02694368, 0, -0.8264554],
//   [0.02493596, 0, -0.814127],
//   [0.02291608, 0, -0.8012022],
//   [0.02090096, 0, -0.7877175],
//   [0.01922631, 0, -0.7760011],
//   [0.01719522, 0, -0.7610738],
//   [0.01561379, 0, -0.7488202],
//   [0.01393056, 0, -0.7350703],
//   [0.01230812, 0, -0.7209972],
//   [0.0109055, 0, -0.7080546],
//   [0.01031232, 0, -0.7023314],
//   [0.008863688, 0, -0.6876105],
//   [0.007499218, 0, -0.6725925],
//   [0.006374836, 0, -0.6591488],
//   [0.005092382, 0, -0.6422638],
//   [0.004234791, 0, -0.6297444],
//   [0.003432274, 0, -0.6168172],
//   [0.002814531, 0, -0.6057892],
//   [0.00221324, 0, -0.5938144],
//   [0.001688957, 0, -0.5819585],
//   [0.001108408, 0, -0.5664015],
//   [0.0006864071, 0, -0.5522527],
//   [0.000449419, 0, -0.5422879],
//   [0.0001885891, 0, -0.5273941],
//   [4.935265E-05, 0, -0.5140162],
//   [7.152557E-07, 0, -0.5017183],
//   [0, 0, -0.4999995],
//   [0, 0, -0.4999995],
//   [0, 0, -0.4999995]
// ]
